import { Injectable, signal, inject, computed } from '@angular/core';
import { supabase } from '../supabase';
import { Appointment, PatientProcess, Patient, Procedure, SurgeryStepConfig, PatientHistoryItem, ProcedureStatusReportItem, MunicipalityIpMaskInput } from '../models';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';
import { AuditService } from './audit.service';
import { OperatorService } from './operator.service';
import { DateUtil } from '../utils/date.util';
import { SecurityUtil } from '../utils/security.util';
import { isValidProxyEndpoint, sanitizeSensitiveProxyData } from '../utils/mask.util';

@Injectable({
    providedIn: 'root'
})
export class AppointmentService {
    private loadingService = inject(LoadingService);
    private toast = inject(ToastService);
    private auditService = inject(AuditService);
    private operatorService = inject(OperatorService);
    // Injetamos DatabaseService via DatabaseService.instance ou via setter se necessário, 
    // mas preferimos usar os serviços especializados diretamente.
    private dbService: any = null;

    setDatabaseService(dbService: any) {
        this.dbService = dbService;
    }

    private formatDateForAudit(dateStr: string | null | undefined): string {
        if (!dateStr) return '-';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            return d.toLocaleDateString('pt-BR');
        } catch {
            return dateStr;
        }
    }

    private getOptimizedAuditData(appointment: Appointment, patient?: any, procedure?: any, executor?: any): any {
        const data: any = {};
        
        if (patient) {
            data.nomePaciente = patient.name || patient.social_name || '-';
            data.patientName = data.nomePaciente;
            data.patientId = patient.id;
            data.cpf = patient.cpf || '-';
            data.telefone = patient.phone || '-';
            data.dataNascimento = this.formatDateForAudit(patient.birthDate);
        }
        
        if (procedure) {
            data.procedimento = procedure.name || '-';
            data.procedureName = procedure.name || '-';
            data.procedureId = procedure.id;
            data.categoria = (procedure.category || '').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) || '-';
        }
        
        if (appointment) {
            data.id = appointment.id;
            data.appointmentId = appointment.id;
            data.appointment_id = appointment.id;
            data.protocol = appointment.latestProtocol;
            data.latestProtocol = appointment.latestProtocol;
            data.status = appointment.status || '-';
            data.prioridade = appointment.priority || 'Normal';
            data.priority = appointment.priority || 'Normal';
            data.dataAgendamento = this.formatDateForAudit(appointment.schedulingData?.date);
            data.horario = appointment.schedulingData?.time || '-';
            data.local = appointment.schedulingData?.location || '-';
            data.location = appointment.schedulingData?.location || '-';
            data.procedureId = data.procedureId || appointment.procedureId;
            data.patientId = data.patientId || appointment.patientId;
            data.urgencia = appointment.urgencyPriority === true;
            if (appointment.urgencyJustification) {
                data.justificativaUrgencia = (appointment.urgencyJustification as any).text || '';
                data.imagemUrgencia = (appointment.urgencyJustification as any).documentImage || '';
            }
        }
        
        if (executor) {
            data.enteExecutor = executor.name || '-';
            data.municipio = executor.municipality_id || '-';
        }
        
        return data;
    }

    /**
     * Converte qualquer representação de data para 'YYYY-MM-DD' (formato aceito pelo Postgres DATE).
     * Aceita: Date, ISO string com timezone, 'DD/MM/YYYY', 'YYYY-MM-DD'.
     * Retorna null se o valor estiver vazio ou inválido.
     */
    static normalizeDate(val: any): string | null {
        if (!val) return null;
        if (val instanceof Date) {
            return isNaN(val.getTime()) ? null : DateUtil.toBrasiliaDateString(val);
        }
        const s = String(val).trim();
        if (!s) return null;
        // DD/MM/YYYY → YYYY-MM-DD
        const brMatch = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (brMatch) return `${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`;
        // ISO / YYYY-MM-DD (com ou sem horário)
        const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})/);
        if (isoMatch) return isoMatch[1];
        return null;
    }

    /**
     * Converte registration_date para ISO timestamp completo (TIMESTAMPTZ).
     * Se já for Date, usa toISOString(). Se for string, normaliza e adiciona T00:00:00.
     */
    static toDbTimestamp(val: any): string {
        return DateUtil.toDbTimestamp(val);
    }

    public appointmentsTable = signal<Appointment[]>([]);
    public totalAppointments = signal<number>(0);
    public queueRefreshTrigger = signal<number>(0);

    triggerQueueRefresh() {
        this.queueRefreshTrigger.update(v => v + 1);
    }

    // ========== PERFORMANCE FIX: Indexes O(1) ==========
    // Caches para lookups rápidos - evita O(n) a cada recálculo
    private patientsIndex = computed(() => {
        const index = new Map<string, Patient>();
        if (this.dbService) {
            for (const p of this.dbService.patientsTable()) {
                index.set(String(p.id), p);
            }
        }
        return index;
    });

    private proceduresIndex = computed(() => {
        const index = new Map<number, Procedure>();
        if (this.dbService) {
            for (const p of this.dbService.proceduresTable()) {
                index.set(p.id, p);
            }
        }
        return index;
    });

    private acsIndex = computed(() => {
        const index = new Map<string, any>();
        if (this.dbService) {
            for (const a of this.dbService.acsTable()) {
                index.set(a.id, a);
            }
        }
        return index;
    });

    private executorsIndex = computed(() => {
        const index = new Map<string, any>();
        if (this.dbService) {
            for (const e of this.dbService.executorsTable()) {
                index.set(String(e.id), e);
            }
        }
        return index;
    });
    // ====================================================

    readonly allPatientsList = computed(() => {
        if (!this.dbService) return [];

        const patients = this.dbService.patientsTable();
        const appointments = this.appointmentsTable();
        const procs = this.dbService.proceduresTable();
        const logs = this.dbService.systemLogsTable();
        const munFilter = this.dbService.dashboardMunicipalityFilter();
        const execFilter = this.dbService.dashboardExecutorFilter();
        const locFilter = this.dbService.dashboardLocationFilter();
        const dashboardDate = this.dbService.dashboardDate();
        const sMonth = dashboardDate.getMonth();
        const sYear = dashboardDate.getFullYear();

        // PERFORMANCE: Usar indexes O(1) em vez de find() O(n)
        const patientsIdx = this.patientsIndex();
        const procsIdx = this.proceduresIndex();
        const acsIdx = this.acsIndex();
        const execsIdx = this.executorsIndex();
        // PERFORMANCE: Map para lookup O(1) de ID -> Appointment (para parentProcedureName)
        const appMap = new Map(appointments.map(a => [a.id, a]));

        // 1. Join Demographics with Appointments
        const baseEnriched: (PatientProcess)[] = appointments
            .filter(a => {
                if (munFilter && a.municipalityId !== munFilter) return false;
                if (execFilter && execFilter !== 'Todos') {
                    const selectedExec = execsIdx.get(String(execFilter));
                    const filterName = (selectedExec?.name || '').toLowerCase().trim();
                    const eName = (a.executorName || '').toLowerCase().trim();
                    const isIdMatch = String(a.executorId) === String(execFilter);
                    const isNameMatch = eName && filterName && (eName.includes(filterName) || filterName.includes(eName));
                    if (!isIdMatch && !isNameMatch) return false;
                }
                if (locFilter && locFilter !== 'Todos') {
                    const lFilter = locFilter.toLowerCase().trim();
                    const sLoc = (a.schedulingData?.location || '').toLowerCase().trim();
                    const oLoc = (a.outcomeData?.location || '').toLowerCase().trim();
                    if (!sLoc.includes(lFilter) && !oLoc.includes(lFilter)) return false;
                }

                // Usar DateUtil para normalizar a data e evitar erros de shift ou formato
                let d = a.outcomeData?.date || a.schedulingData?.date || a.requestDate;
                if (d) {
                    const normalized = DateUtil.toBrasiliaDateString(d);
                    if (normalized) {
                        const [y, m] = normalized.split('-').map(Number);
                        // sMonth é 0-indexed, m é 1-indexed. Comparação: m-1 === sMonth
                        if (m - 1 !== sMonth || y !== sYear) {
                            // Se não for do mês selecionado, só mostra se estiver em fila (Aguardando/Em Agendamento)
                            if (a.status !== 'Aguardando' && a.status !== 'Em Agendamento') return false;
                        }
                    }
                }
                return true;
            })
            .map(a => {
                const patient = patientsIdx.get(String(a.patientId));
                const proc = procsIdx.get(a.procedureId);
                const acsInfo = patient?.acsId ? acsIdx.get(patient.acsId) : null;

                // Lookup O(1) do nome do procedimento pai
                let parentProcedureName: string | undefined;
                if (a.parentAppointmentId) {
                    const parentApp = appMap.get(a.parentAppointmentId);
                    if (parentApp) {
                        parentProcedureName = procsIdx.get(parentApp.procedureId)?.name;
                    }
                }

                return {
                    ...(patient || { id: 0, cpf: '', name: 'Excluído', susCard: '', birthDate: '', phone: '', address: { zip: '', street: '', number: '', district: '', city: '', uf: '' }, municipalityId: '', registrationDate: new Date() }),
                    ...a,
                    isUrgency: !!a.urgencyPriority,
                    procedureName: proc?.name || 'Desconhecido',
                    category: (proc?.category || 'Geral').toUpperCase(),
                    acsName: acsInfo ? acsInfo.name : undefined,
                    acsId: patient?.acsId,
                    acsContact: acsInfo ? acsInfo.contactNumber : undefined,
                    requiresPreSurgery: proc?.requiresPreSurgery,
                    preSurgeryCount: proc?.preSurgeryCount,
                    hasOrientation: !!proc?.hasOrientation,
                    orientationText: proc?.orientationText,
                    daysSinceStatusChange: 0,
                    latestProtocol: (a.parentAppointmentId ? (a.subProtocol || a.latestProtocol) : a.latestProtocol) || '-',
                    parentProcedureName
                };
            });

        // 1.1 PERFORMANCE: Group steps by parentId in ONE pass (O(N)) instead of filter() inside map (O(N^2))
        const stepsByParent = new Map<number, PatientProcess[]>();
        baseEnriched.forEach(s => {
            if (s.parentAppointmentId) {
                const list = stepsByParent.get(s.parentAppointmentId) || [];
                list.push(s);
                stepsByParent.set(s.parentAppointmentId, list);
            }
        });

        const enrichedAppointments = baseEnriched.map(app => {
            if (app.isSurgeryParent) {
                const steps = stepsByParent.get(app.id) || [];
                return {
                    ...app,
                    steps: steps.sort((a, b) => (a.currentStepIndex || 0) - (b.currentStepIndex || 0))
                };
            }
            return app;
        });

        // 2. Coletamos a lista enriquecida (a ordenação principal virá do banco de dados na Fila)
        const flatSortedList = [...enrichedAppointments];
        const procedureQueues: Record<number, number> = {};

        // 3. Final mapping with calculated stats (PERFORMANCE: history excluded - computed on-demand)
        const brNow = DateUtil.getBrasiliaNow();
        const todayStart = new Date(brNow);
        todayStart.setHours(0, 0, 0, 0);

        const getDaysDiff = (dateStr: string | Date | undefined): number => {
            if (!dateStr) return 0;
            let targetDate: Date;
            if (dateStr instanceof Date) {
                targetDate = new Date(dateStr.getFullYear(), dateStr.getMonth(), dateStr.getDate());
            } else {
                const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/) || String(dateStr).match(/^(\d{2})\/(\d{2})\/(\d{4})/);
                if (!match) return 0;
                const isISO = String(dateStr).match(/^\d{4}/);
                const [y, m, d] = isISO
                    ? [parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10)]
                    : [parseInt(match[3], 10), parseInt(match[2], 10) - 1, parseInt(match[1], 10)];
                targetDate = new Date(y, m, d);
            }
            if (isNaN(targetDate.getTime())) return 0;
            return Math.floor((todayStart.getTime() - targetDate.getTime()) / (1000 * 60 * 60 * 24));
        };

        return flatSortedList.map((app) => {
            const daysSinceReg = getDaysDiff(app.registrationDate);
            const daysSinceReq = getDaysDiff(app.requestDate);

            let daysSinceStatusChange = 0;
            if (app.status === 'Em Agendamento') {
                daysSinceStatusChange = app.schedulingStartedAt ? getDaysDiff(app.schedulingStartedAt) : daysSinceReg;
            } else if (app.status === 'Aguardando') {
                daysSinceStatusChange = daysSinceReg;
            } else if (app.status === 'Agendado' && app.schedulingData?.date) {
                daysSinceStatusChange = getDaysDiff(app.schedulingData.date);
            } else if ((app.status === 'Realizado' || app.status === 'Não Realizado') && app.outcomeData?.date) {
                daysSinceStatusChange = getDaysDiff(app.outcomeData.date);
            }

            const limit = app.category === 'CIRURGIA' ? 180 : 100;
            const isExpired = daysSinceReq > limit;
            const isNearExpiration = !isExpired && daysSinceReq >= limit - 7;

            let estimatedDays = 0;
            let currentQueuePosition = 0;
            if (app.status === 'Aguardando' || app.status === 'Em Agendamento') {
                currentQueuePosition = (procedureQueues[app.procedureId] || 0) + 1;
                procedureQueues[app.procedureId] = currentQueuePosition;

                const proc = procsIdx.get(app.procedureId);
                const limitProc = (proc?.monthlyLimit || 0) > 0 ? proc!.monthlyLimit : 1;
                estimatedDays = app.status === 'Em Agendamento' ? 1 : Math.ceil(currentQueuePosition / limitProc) * 30;
            }

            return {
                ...app,
                position: (app.status === 'Aguardando' || app.status === 'Em Agendamento') ? currentQueuePosition : 0,
                estimatedWaitDays: estimatedDays,
                daysSinceRegistration: daysSinceReg,
                daysSinceRequest: daysSinceReq,
                daysSinceStatusChange,
                isExpired,
                isNearExpiration,
                history: [] // PERF: History computed on-demand in buildPatientHistory()
            } as PatientProcess;
        }).sort((a, b) => {
            if (a.status === 'Em Agendamento' && b.status !== 'Em Agendamento') return -1;
            if (a.status !== 'Em Agendamento' && b.status === 'Em Agendamento') return 1;
            return 0;
        });
    });

    /**
     * PERFORMANCE: On-demand history builder. Call only when opening patient detail modal.
     * This was previously computed for ALL patients simultaneously inside allPatientsList(),
     * causing severe Chrome thread blocking.
     */
    buildPatientHistory(app: PatientProcess, allAppointments?: PatientProcess[]): PatientHistoryItem[] {
        if (!this.dbService) return [];

        const logs = this.dbService.systemLogsTable();
        const patientsIdx = this.patientsIndex();
        const enrichedAppointments = allAppointments || this.allPatientsList();

        // 1. System logs related to this patient/appointment
        const itemLogs = logs.filter((l: any) => {
            if (!l.details) return false;
            const d = typeof l.details === 'string' ? JSON.parse(l.details) : l.details;
            const pId = app.patientId;
            const aId = app.id;
            if (d.patientId == pId || d.patient_id == pId) return true;
            if (d.appointmentId == aId || d.appointment_id == aId) return true;
            if (l.protocol && app.latestProtocol && l.protocol === app.latestProtocol) return true;
            if (d.changes && (d.changes.id == pId || d.changes.id == aId)) return true;
            if (d.deletedId == pId || d.deletedId == aId) return true;
            return false;
        }) as PatientHistoryItem[];

        // 2. Audit trail from the appointment record
        const auditLogs: PatientHistoryItem[] = [];
        const mapAuditEntry = (t: any, prefix: string, status: string): PatientHistoryItem => {
            const dateStr = t.date && typeof t.date === 'string' && t.date.includes('/') ? t.date.split('/').reverse().join('-') : t.date;
            const fullDateStr = t.time ? `${dateStr}T${t.time}`.trim() : dateStr;
            let parsedDate = new Date();
            if (fullDateStr) {
                const parsed = new Date(fullDateStr);
                if (!isNaN(parsed.getTime())) parsedDate = parsed;
            }
            return {
                date: parsedDate.toISOString(),
                event: `${prefix}${t.action}`,
                details: typeof t.details === 'string' ? t.details : JSON.stringify(t.details || {}),
                status,
                protocol: t.protocol
            };
        };

        if (app.auditTrail && Array.isArray(app.auditTrail)) {
            auditLogs.push(...app.auditTrail.map((t: any) => mapAuditEntry(t, '', 'Histórico')));
        }

        const patientObj = patientsIdx.get(String(app.patientId));
        if (patientObj?.auditTrail && Array.isArray(patientObj.auditTrail)) {
            auditLogs.push(...patientObj.auditTrail.map((t: any) => mapAuditEntry(t, '(Paciente) ', 'Cadastro')));
        }

        // 3. Other appointments for the same patient
        const appHistory = enrichedAppointments
            .filter(a => a.patientId === app.patientId)
            .map(a => {
                const regDate = a.registrationDate instanceof Date && !isNaN(a.registrationDate.getTime())
                    ? a.registrationDate
                    : new Date(a.registrationDate);
                return {
                    date: !isNaN(regDate.getTime()) ? regDate.toISOString() : new Date().toISOString(),
                    event: `Processo: ${a.procedureName}`,
                    details: `Categoria: ${a.category} - Status: ${a.status}`,
                    status: a.status,
                    protocol: a.latestProtocol
                } as PatientHistoryItem;
            });

        return [...itemLogs, ...appHistory, ...auditLogs]
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }

    readonly pendingRealizations = computed(() => {
        if (!this.dbService) return [];
        const appointments = this.appointmentsTable();
        const procs = this.dbService.proceduresTable();
        const munFilter = this.dbService.dashboardMunicipalityFilter();
        const execFilter = this.dbService.dashboardExecutorFilter();
        const locFilter = this.dbService.dashboardLocationFilter();
        const procFilter = this.dbService.dashboardProcedureFilter();
        const catFilter = this.dbService.dashboardCategoryFilter();

        const patientMap = new Map<number, any>(this.dbService.patientsTable().map((p: any) => [p.id, p]));
        const procMap = new Map<number, any>(procs.map((p: any) => [p.id, p]));
        const appointmentMap = new Map<number, Appointment>(appointments.map(a => [a.id, a]));

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let filtered = appointments.filter(a => {
            if (munFilter && a.municipalityId !== munFilter) return false;
            if (a.status !== 'Agendado') return false;
            if (!a.schedulingData?.date) return false;

            const [y, m, d] = a.schedulingData.date.split('-').map(Number);
            const scheduledDate = new Date(y, m - 1, d);
            if (scheduledDate > today) return false; // > em vez de >=: inclui agendamentos DO DIA (data == hoje)

            const dayFilter = this.dbService.dashboardDayFilter();
            const dashDate = this.dbService.dashboardDate();
            if (dayFilter && String(dayFilter) !== 'null') {
                if (d !== Number(dayFilter) || m !== (dashDate.getMonth() + 1) || y !== dashDate.getFullYear()) {
                    return false;
                }
            }

            if (execFilter && execFilter !== 'Todos') {
                const selectedExec = this.dbService.executorsTable().find((e: any) => String(e.id) === String(execFilter));
                const filterName = (selectedExec?.name || '').toLowerCase().trim();
                const eName = (a.executorName || '').toLowerCase().trim();
                const isIdMatch = String(a.executorId) === String(execFilter);
                const isNameMatch = eName && filterName && (eName.includes(filterName) || filterName.includes(eName));
                if (!isIdMatch && !isNameMatch) return false;
            }
            if (locFilter && locFilter !== 'Todos') {
                const lFilter = locFilter.toLowerCase().trim();
                const sLoc = (a.schedulingData?.location || '').toLowerCase().trim();
                if (!sLoc.includes(lFilter)) return false;
            }
            return true;
        });

        if (procFilter) {
            filtered = filtered.filter(a => a.procedureId === procFilter);
        } else if (catFilter && catFilter !== 'Todas as Categorias') {
            const validProcIds = procs.filter(p => p.category === catFilter).map(p => p.id);
            filtered = filtered.filter(a => validProcIds.includes(a.procedureId));
        }

        return filtered.map(a => {
            const patient = patientMap.get(a.patientId);
            const procedure = procMap.get(a.procedureId);
            let parentProcedureName: string | undefined;
            if (a.parentAppointmentId) {
                const parentApp = appointmentMap.get(a.parentAppointmentId);
                if (parentApp) parentProcedureName = procMap.get(parentApp.procedureId)?.name;
            }

            const safePatient = patient || {
                id: 0, cpf: '', name: 'Paciente Não Encontrado', susCard: '---',
                birthDate: '', phone: '',
                address: { zip: '', street: '', number: '', district: '', city: '', uf: '' },
                municipalityId: '', registrationDate: new Date()
            } as any;

            const acsInfo = patient?.acsId ? this.dbService.acsTable().find((ac: any) => ac.id === patient.acsId) : null;

            return {
                ...safePatient,
                ...a,
                procedureName: procedure?.name || 'Procedimento Desconhecido',
                category: (procedure?.category || 'OUTROS').toUpperCase(),
                parentProcedureName,
                acsName: acsInfo ? acsInfo.name : undefined,
                acsContact: acsInfo ? acsInfo.contactNumber : undefined
            } as PatientProcess;
        }).sort((a, b) => {
            if (a.status === 'Em Agendamento' && b.status !== 'Em Agendamento') return -1;
            if (a.status !== 'Em Agendamento' && b.status === 'Em Agendamento') return 1;
            return 0;
        });
    });

    readonly pendingRealizationsCount = computed(() => this.pendingRealizations().length);

    /**
     * Mapeia linha tabular retornada por RPCs (fetch_queue_paginated / fetch_pending_realizations_paginated)
     * para a interface completa PatientProcess.
     */
    public mapTabularRowToPatientProcess(a: any, municipalityIdFallback?: string): PatientProcess {
        const schedStr = a.schedule_date;
        const schedulingData = schedStr ? {
            date: schedStr.split('T')[0],
            time: a.schedule_time || schedStr.split('T')[1]?.substring(0, 5) || '',
            location: a.schedule_location
        } : null;

        const outStr = a.outcome_date;
        const outcomeData = (outStr || a.outcome_reason) ? {
            date: outStr ? outStr.split('T')[0] : undefined,
            reason: a.outcome_reason,
            location: a.outcome_location
        } : null;

        const today = new Date();
        const regDateStr = a.registration_date;
        let regDate = new Date();
        if (regDateStr) {
            const d = new Date(regDateStr);
            regDate = isNaN(d.getTime()) ? new Date() : d;
        }

        let daysSinceStatusChange = 0;
        if (a.status === 'Agendado' && a.schedule_date) {
            const sDate = new Date(a.schedule_date);
            daysSinceStatusChange = Math.ceil((sDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        } else if (a.status === 'Realizado' && a.outcome_date) {
            const oDate = new Date(a.outcome_date);
            daysSinceStatusChange = Math.floor((today.getTime() - oDate.getTime()) / (1000 * 60 * 60 * 24));
        } else if (a.status === 'Em Agendamento') {
            const schedStartStr = a.scheduling_started_at || a.schedulingStartedAt;
            const startDate = schedStartStr ? new Date(schedStartStr) : regDate;
            daysSinceStatusChange = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        } else if (a.status === 'Aguardando' || a.status === 'Cancelado' || a.status === 'Não Realizado') {
            daysSinceStatusChange = Math.floor((today.getTime() - regDate.getTime()) / (1000 * 60 * 60 * 24));
        }

        return {
            ...a,
            id: a.id,
            patientId: a.patient_id ?? a.patientId,
            procedureId: a.procedure_id ?? a.procedureId,
            municipalityId: a.municipality_id || a.f_municipality_id || a.municipalityId || municipalityIdFallback || this.dbService?.currentMunicipalityId?.() || '',
            status: a.status,
            priority: a.priority,

            registrationDate: a.registration_date,
            requestDate: a.request_date,
            schedulingData: schedulingData,
            outcomeData: outcomeData,
            schedulingStartedAt: a.scheduling_started_at || a.schedulingStartedAt,

            urgencyPriority: a.urgency_priority,
            isUrgency: a.urgency_priority,
            latestProtocol: a.latest_protocol,
            preSurgeryCompleted: a.pre_surgery_completed,
            preSurgeryCount: a.pre_surgery_count,
            daysSinceStatusChange: daysSinceStatusChange,

            name: a.patient_name,
            socialName: a.patient_social_name,
            cpf: a.patient_cpf,
            phone: a.patient_phone,
            birthDate: a.patient_birth_date,
            susCard: a.patient_sus_card,
            address: {
                zip: a.patient_zip,
                street: a.patient_street,
                number: a.patient_number,
                complemento: a.patient_complement,
                district: a.patient_district,
                city: a.patient_city,
                uf: a.patient_uf
            },

            procedureName: a.procedure_name,
            category: a.procedure_category,
            parentProcedureName: a.parent_procedure_name,

            parentAppointmentId: a.parent_appointment_id ?? a.parentAppointmentId,
            isSurgeryParent: a.is_surgery_parent ?? a.isSurgeryParent,
            currentStepIndex: a.current_step_index ?? a.currentStepIndex,
            subProtocol: a.sub_protocol ?? a.subProtocol,

            acsId: a.acs_id,
            acsName: a.acs_name,
            executorId: a.executor_id,
            executorName: a.executor_name,
            originLocation: a.origin_location,
            professionalName: a.professional_name,
            anatomicalRegion: a.anatomical_region,
            allocatedProfessionalId: a.allocated_professional_id,
            allocatedDate: a.allocated_date,
            allocatedTime: a.allocated_time
        };
    }

    /**
     * Busca agendamentos de forma paginada para o dashboard.
     */
    async fetchAppointmentsPaginated(municipalityId: string | null, statusGroup: string[], limit: number, offset: number, abortSignal?: AbortSignal, hideLoading: boolean = false) {
        try {
            if (!hideLoading) this.loadingService.set(true);
            const { data, error } = await supabase.rpc('fetch_appointments_paginated', {
                p_municipality_id: municipalityId,
                p_status_group: statusGroup,
                p_limit: limit,
                p_offset: offset
            });

            if (error) throw error;

            const result = (data as any)?.[0];
            return {
                data: (result?.data || []).map((a: any) => this.mapDbToAppointment(a)),
                total_count: Number(result?.total_count || 0)
            };
        } catch (err: any) {
            // Se for abortado pelo componente, retornamos silenciosamente
            if (err.name === 'AbortError' || err.message === 'Fetch is aborted' || err.code === 'FETCH_ABORTED') return { data: [], total_count: 0 };

            console.error('[AppointmentService] Erro na busca paginada:', err);
            this.toast.show('Erro ao carregar agendamentos.', 'error');
            return { data: [], total_count: 0 };
        } finally {
            if (!hideLoading) this.loadingService.set(false);
        }
    }

    /**
     * Busca a fila de espera de forma paginada com filtros e ordenação complexa.
     */
    async fetchQueuePaginated(municipalityId: string | null, params: { procedureId?: number, category?: string, priority?: string, searchTerm?: string, locationId?: string, originLocation?: string, statusGroup?: string[], dateFilter?: string, acsId?: string, executorId?: string, limit: number, offset: number, forceRegistrationFilter?: boolean, orderByStatus?: boolean, excludeUbsCategories?: boolean, abortSignal?: AbortSignal, hideLoading?: boolean }) {
        try {
            if (!params.hideLoading) this.loadingService.set(true);
            let finalSearchTerm = params.searchTerm?.trim() || null;
            // Se o termo parece um CPF (apenas números, pontos e traços), normalizamos para facilitar a busca no banco
            if (finalSearchTerm && /^[\d\.\-\s]+$/.test(finalSearchTerm.trim()) && finalSearchTerm.trim().replace(/\D/g, '').length >= 3) {
                finalSearchTerm = finalSearchTerm.trim().replace(/\D/g, '');
            }

            const { data, error } = await supabase.rpc('fetch_queue_paginated', {
                p_municipality_id: municipalityId,
                p_procedure_id: params.procedureId,
                p_category: params.category,
                p_priority_filter: params.priority,
                p_search_term: finalSearchTerm,
                p_location_id: params.locationId?.toString() || null,
                p_status_group: params.statusGroup || ['Aguardando', 'Em Agendamento'],
                p_date_filter: params.dateFilter || null, // Fix: impede envio de string vazia gerando Erro 400
                p_limit: params.limit,
                p_offset: params.offset,
                p_acs_id: params.acsId || null, // Filtro por ACS
                p_executor_id: params.executorId || null, // Filtro por Executor
                p_force_registration_filter: params.forceRegistrationFilter || false,
                p_order_by_status: params.orderByStatus || false,
                p_exclude_ubs_categories: params.excludeUbsCategories || false,
                p_origin_location: params.originLocation || null
            });

            if (error) throw error;

            const totalCount = data && data.length > 0 ? Number(data[0].total_count || 0) : 0;

            return {
                data: (data || []).map((a: any) => this.mapTabularRowToPatientProcess(a, municipalityId || undefined)),
                total_count: totalCount
            };
        } catch (err: any) {
            // Se for abortado pelo componente, retornamos silenciosamente
            if (err.name === 'AbortError' || err.message === 'Fetch is aborted' || err.code === 'FETCH_ABORTED') return { data: [], total_count: 0 };

            console.error('[AppointmentService] Erro na busca da fila paginada:', err);
            this.toast.show('Erro ao carregar fila de espera.', 'error');
            return { data: [], total_count: 0 };
        } finally {
            if (!params.hideLoading) this.loadingService.set(false);
        }
    }

    /**
     * Mapeia linha retornada por query direta PostgREST com joins para PatientProcess.
     */
    public mapDirectPendingRowToProcess(a: any): PatientProcess {
        const p = a.patients || {};
        const pr = a.procedures || {};
        const schedStr = a.schedule_date;
        const schedulingData = schedStr ? {
            date: schedStr.split('T')[0],
            time: a.schedule_time || schedStr.split('T')[1]?.substring(0, 5) || '',
            location: a.schedule_location
        } : null;

        const outStr = a.outcome_date;
        const outcomeData = (outStr || a.outcome_reason) ? {
            date: outStr ? outStr.split('T')[0] : undefined,
            reason: a.outcome_reason,
            location: a.outcome_location
        } : null;

        const today = new Date();
        let daysSinceStatusChange = 0;
        if (a.schedule_date) {
            const sDate = new Date(a.schedule_date);
            daysSinceStatusChange = Math.ceil((sDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        }

        return {
            ...a,
            id: a.id,
            patientId: a.patient_id,
            procedureId: a.procedure_id,
            municipalityId: a.municipality_id,
            status: a.status,
            priority: a.priority,

            registrationDate: a.registration_date,
            requestDate: a.request_date,
            schedulingData: schedulingData,
            outcomeData: outcomeData,

            urgencyPriority: a.urgency_priority,
            isUrgency: a.urgency_priority,
            latestProtocol: a.latest_protocol,
            preSurgeryCompleted: a.pre_surgery_completed,
            daysSinceStatusChange: daysSinceStatusChange,

            name: p.name || 'Paciente Não Encontrado',
            socialName: p.social_name,
            cpf: p.cpf,
            phone: p.phone,
            birthDate: p.birth_date,
            susCard: p.sus_card,
            address: {
                zip: p.address_zip,
                street: p.address_street,
                number: p.address_number,
                complemento: p.address_complement,
                district: p.address_district,
                city: p.address_city,
                uf: p.address_uf
            },

            procedureName: pr.name || 'Procedimento Desconhecido',
            category: (pr.category || 'OUTROS').toUpperCase(),

            parentAppointmentId: a.parent_appointment_id ?? a.parentAppointmentId,
            isSurgeryParent: a.is_surgery_parent ?? a.isSurgeryParent,
            currentStepIndex: a.current_step_index ?? a.currentStepIndex,
            subProtocol: a.sub_protocol ?? a.subProtocol,

            acsId: p.acs_id,
            acsName: a.acs_name,
            executorId: a.executor_id,
            executorName: a.executor_name,
            originLocation: a.origin_location,
            anatomicalRegion: a.anatomical_region,
            allocatedProfessionalId: a.allocated_professional_id,
            allocatedDate: a.allocated_date,
            allocatedTime: a.allocated_time
        };
    }

    /**
     * Busca realizações pendentes de forma paginada (server-side).
     */
    async fetchPendingRealizationsPaginated(municipalityId: string | null, params: {
        category?: string;
        acsId?: string;
        searchTerm?: string;
        procedureId?: number | null;
        location?: string | null;
        startDate?: string | null;
        endDate?: string | null;
        limit: number;
        offset: number;
        excludeUbsCategories?: boolean;
        abortSignal?: AbortSignal;
        hideLoading?: boolean;
    }) {
        try {
            if (!params.hideLoading) this.loadingService.set(true);
            let finalSearchTerm = params.searchTerm ? SecurityUtil.sanitizeSearchTerm(params.searchTerm) : null;
            if (finalSearchTerm && /^[\d\.\-\s]+$/.test(finalSearchTerm.trim()) && finalSearchTerm.trim().replace(/\D/g, '').length >= 3) {
                finalSearchTerm = finalSearchTerm.trim().replace(/\D/g, '');
            }

            // 1. Tenta a RPC otimizada primeiro
            try {
                const rpcPayload: any = {
                    p_municipality_id: municipalityId,
                    p_category: params.category || null,
                    p_acs_id: params.acsId || null,
                    p_search_term: finalSearchTerm,
                    p_limit: params.limit,
                    p_offset: params.offset,
                    p_exclude_ubs_categories: params.excludeUbsCategories || false
                };
                if (params.procedureId) rpcPayload.p_procedure_id = params.procedureId;
                if (params.location && params.location !== 'Todos') rpcPayload.p_location = params.location;
                if (params.startDate) rpcPayload.p_start_date = params.startDate;
                if (params.endDate) rpcPayload.p_end_date = params.endDate;

                const { data, error } = await supabase.rpc('fetch_pending_realizations_paginated', rpcPayload);

                if (!error && data) {
                    const totalCount = data.length > 0 ? Number(data[0].total_count || 0) : 0;
                    return {
                        data: data.map((a: any) => this.mapTabularRowToPatientProcess(a, municipalityId || undefined)),
                        total_count: totalCount
                    };
                }
            } catch (rpcErr) {
                // Fallback para query direta caso a RPC não esteja disponível
            }

            // 2. Fallback resiliente via PostgREST direto (busca com JOIN e paginação rápida)
            const todayStr = DateUtil.getBrasiliaNow().toISOString().split('T')[0];
            let query = supabase
                .from('appointments')
                .select(`
                    id,
                    patient_id,
                    procedure_id,
                    municipality_id,
                    status,
                    priority,
                    registration_date,
                    request_date,
                    request_image_url,
                    schedule_date,
                    schedule_time,
                    schedule_location,
                    outcome_date,
                    outcome_location,
                    outcome_reason,
                    urgency_priority,
                    latest_protocol,
                    pre_surgery_completed,
                    anatomical_region,
                    parent_appointment_id,
                    is_surgery_parent,
                    sub_protocol,
                    current_step_index,
                    executor_id,
                    executor_name,
                    allocated_professional_id,
                    allocated_date,
                    allocated_time,
                    patients!inner(
                        name,
                        social_name,
                        cpf,
                        phone,
                        birth_date,
                        sus_card,
                        address_zip,
                        address_street,
                        address_number,
                        address_complement,
                        address_district,
                        address_city,
                        address_uf,
                        acs_id
                    ),
                    procedures!inner(
                        name,
                        category
                    )
                `, { count: 'exact' })
                .eq('status', 'Agendado')
                .lte('schedule_date', todayStr)
                .neq('is_active', false);

            if (municipalityId) {
                query = query.eq('municipality_id', municipalityId);
            }

            if (params.category && params.category !== 'Todas as Categorias') {
                query = query.eq('procedures.category', params.category);
            }

            if (params.procedureId) {
                query = query.eq('procedure_id', params.procedureId);
            }

            if (params.location && params.location !== 'Todos') {
                query = query.ilike('schedule_location', `%${params.location.trim()}%`);
            }

            if (params.startDate) {
                query = query.gte('schedule_date', `${params.startDate}T00:00:00`);
            }

            if (params.endDate) {
                query = query.lte('schedule_date', `${params.endDate}T23:59:59.999Z`);
            }

            if (params.acsId && params.acsId !== 'null' && params.acsId !== 'TODOS') {
                query = query.eq('patients.acs_id', params.acsId);
            }

            if (params.excludeUbsCategories) {
                query = query.not('procedures.category', 'ilike', '%CONSULTA BÁSICA%');
            }

            if (finalSearchTerm) {
                if (/^\d+$/.test(finalSearchTerm)) {
                    query = query.or(`cpf.ilike.%${finalSearchTerm}%,latest_protocol.ilike.%${finalSearchTerm}%`, { referencedTable: 'patients' });
                } else {
                    query = query.ilike('patients.name', `%${finalSearchTerm}%`);
                }
            }

            if (params.abortSignal) {
                query = query.abortSignal(params.abortSignal);
            }

            query = query
                .order('schedule_date', { ascending: false })
                .range(params.offset, params.offset + params.limit - 1);

            const { data, count, error } = await query;

            if (error) throw error;

            return {
                data: (data || []).map((a: any) => this.mapDirectPendingRowToProcess(a)),
                total_count: count || 0
            };
        } catch (err: any) {
            if (err.name === 'AbortError' || err.message === 'Fetch is aborted' || err.code === 'FETCH_ABORTED') {
                return { data: [], total_count: 0 };
            }
            console.error('[AppointmentService] Erro ao buscar realizações pendentes paginadas:', err);
            return { data: [], total_count: 0 };
        } finally {
            if (!params.hideLoading) this.loadingService.set(false);
        }
    }

    /**
     * Busca TODOS os registros da fila sequencialmente (bypass do limite de 1000 do PostgREST).
     * Útil para exportações completas.
     */
    async fetchAllQueueRecords(municipalityId: string | null, params: any) {
        let allData: any[] = [];
        let offset = 0;
        const limit = 1000;
        let totalItems = 0;
        let hasMore = true;

        try {
            // Mostramos o loading global apenas uma vez
            this.loadingService.set(true);

            while (hasMore) {
                const response = (await this.fetchQueuePaginated(municipalityId, { 
                    ...params, 
                    limit, 
                    offset, 
                    hideLoading: true // Não deixa o fetch individual resetar o loading global
                })) as { data: any[], total_count: number };

                if (response.data && response.data.length > 0) {
                    allData = [...allData, ...response.data];
                    totalItems = response.total_count;
                    offset += limit;
                    hasMore = offset < totalItems;
                } else {
                    hasMore = false;
                }
            }

            return { data: allData, total_count: totalItems };
        } catch (err) {
            console.error('[AppointmentService] Erro na busca sequencial:', err);
            return { data: allData, total_count: allData.length };
        } finally {
            this.loadingService.set(false);
        }
    }

    /**
     * Obtém resumo estatístico para o dashboard.
     */
    async getDashboardSummary(municipalityId: string | null, year: number, month: number, filters?: { category?: string, procedureId?: number, acsId?: string, executorId?: string, excludeUbsCategories?: boolean, day?: number }) {
        try {
            const { data, error } = await supabase.rpc('get_dashboard_summary', {
                p_municipality_id: municipalityId,
                p_year: year,
                p_month: month,
                p_day: filters?.day || null,
                p_category: filters?.category || null,
                p_procedure_id: filters?.procedureId || null,
                p_acs_id: filters?.acsId || null,
                p_executor_id: filters?.executorId || null,
                p_exclude_ubs_categories: filters?.excludeUbsCategories || false
            });

            if (error) throw error;
            return data;
        } catch (err: any) {
            console.error('[AppointmentService] Erro ao carregar resumo:', err);
            this.toast.show('Erro Banco de Dados (Dashboard): ' + err.message, 'error');
            return { waiting: 0, scheduled: 0, done: 0, canceled: 0, requests: 0, year_done: 0, pending_realizations: 0, total_patients: 0 };
        }
    }

    /**
     * Obtém relatório consolidado de procedimentos e quantitativos absolutos de pacientes por status.
     * Tenta execução de alta performance via RPC PostgreSQL (get_procedure_status_report)
     * e provê fallback resiliente direto via queue_counters e procedures caso a RPC ainda não esteja instalada.
     */
    async getProcedureStatusReport(
        municipalityId: string | null,
        year?: number,
        month?: number,
        excludeUbsCategories: boolean = false
    ): Promise<ProcedureStatusReportItem[]> {
        try {
            const { data, error } = await supabase.rpc('get_procedure_status_report', {
                p_municipality_id: municipalityId,
                p_year: year || null,
                p_month: month || null,
                p_exclude_ubs_categories: excludeUbsCategories
            });

            if (!error && Array.isArray(data) && data.length > 0) {
                return data.map((r: any) => ({
                    procedureId: Number(r.procedure_id),
                    procedureName: r.procedure_name || 'Sem nome',
                    category: r.category || 'OUTROS',
                    waitingCount: Number(r.waiting_count) || 0,
                    inSchedulingCount: Number(r.in_scheduling_count) || 0,
                    scheduledCount: Number(r.scheduled_count) || 0,
                    doneCount: Number(r.done_count) || 0,
                    notDoneCount: Number(r.not_done_count) || 0,
                    canceledCount: Number(r.canceled_count) || 0,
                    dispensedCount: Number(r.dispensed_count) || 0,
                    totalCount: Number(r.total_count) || 0,
                    monthScheduledCount: Number(r.month_scheduled_count) || 0,
                    monthDoneCount: Number(r.month_done_count) || 0,
                    monthCanceledCount: Number(r.month_canceled_count) || 0,
                    monthRequestsCount: Number(r.month_requests_count) || 0,
                    monthTotal: Number(r.month_total) || ((Number(r.month_scheduled_count) || 0) + (Number(r.month_done_count) || 0) + (Number(r.month_canceled_count) || 0)),
                    scheduledExecutors: r.scheduled_executors || undefined
                }));
            }
        } catch (rpcErr) {
            console.warn('[AppointmentService] RPC get_procedure_status_report falhou ou não existe, utilizando fallback:', rpcErr);
        }

        return this.getProcedureStatusReportFallback(municipalityId, year, month, excludeUbsCategories);
    }

    /**
     * Agregação resiliente dos números de pacientes por procedimento e status.
     */
    private async getProcedureStatusReportFallback(
        municipalityId: string | null,
        year?: number,
        month?: number,
        excludeUbsCategories: boolean = false
    ): Promise<ProcedureStatusReportItem[]> {
        try {
            if (!municipalityId) return [];

            // 1. Busca procedimentos cadastrados para o município (ou GLOBAL)
            const { data: procs, error: procErr } = await supabase
                .from('procedures')
                .select('id, name, category, is_active')
                .or(`municipality_id.eq.${municipalityId},municipality_id.eq.GLOBAL`)
                .order('category')
                .order('name');

            if (procErr) throw procErr;

            const filteredProcs = (procs || []).filter(p => {
                if (p.is_active === false) return false;
                if (excludeUbsCategories && p.category && p.category.toUpperCase().includes('CONSULTA BÁSICA')) return false;
                return true;
            });

            // 2. Busca contadores oficiais da tabela queue_counters (acumulado de todos os tempos)
            const { data: counters, error: countErr } = await supabase
                .from('queue_counters')
                .select('procedure_id, status, total_count')
                .eq('municipality_id', municipalityId);

            if (countErr) throw countErr;

            const reportMap = new Map<number, ProcedureStatusReportItem>();
            for (const p of filteredProcs) {
                reportMap.set(p.id, {
                    procedureId: p.id,
                    procedureName: p.name?.trim() || 'Sem nome',
                    category: p.category || 'OUTROS',
                    waitingCount: 0,
                    inSchedulingCount: 0,
                    scheduledCount: 0,
                    doneCount: 0,
                    notDoneCount: 0,
                    canceledCount: 0,
                    dispensedCount: 0,
                    totalCount: 0,
                    monthScheduledCount: 0,
                    monthDoneCount: 0,
                    monthCanceledCount: 0,
                    monthRequestsCount: 0
                });
            }

            for (const c of counters || []) {
                const item = reportMap.get(c.procedure_id);
                if (!item) continue;
                const cnt = Number(c.total_count) || 0;
                switch (c.status) {
                    case 'Aguardando': item.waitingCount += cnt; break;
                    case 'Em Agendamento': item.inSchedulingCount += cnt; break;
                    case 'Agendado': item.scheduledCount += cnt; break;
                    case 'Realizado': item.doneCount += cnt; break;
                    case 'Não Realizado': item.notDoneCount += cnt; break;
                    case 'Cancelado': item.canceledCount += cnt; break;
                    case 'Dispensado': item.dispensedCount += cnt; break;
                }
                item.totalCount += cnt;
            }

            // Busca executores cadastrados para mapeamento fidedigno de IDs e nomes
            const execIdMap = new Map<string, string>();
            const registeredExecNames: string[] = [];
            try {
                const { data: dbExecs } = await supabase
                    .from('executors')
                    .select('id, name')
                    .or(`municipality_id.eq.${municipalityId},municipality_id.eq.GLOBAL`);

                if (dbExecs && Array.isArray(dbExecs)) {
                    for (const e of dbExecs) {
                        if (e.id && e.name) {
                            const n = e.name.trim().toUpperCase();
                            execIdMap.set(String(e.id), n);
                            if (!registeredExecNames.includes(n)) registeredExecNames.push(n);
                        }
                    }
                }
            } catch (execErr) {
                console.warn('[AppointmentService] Erro não-bloqueante ao carregar tabela de executores:', execErr);
            }

            const resolveExecutorName = (a: any): string => {
                const en = (a.executor_name || '').trim().toUpperCase();
                const eid = a.executor_id ? String(a.executor_id) : '';
                const eidName = eid ? execIdMap.get(eid) : null;
                const loc = (a.schedule_location || '').trim().toUpperCase();

                // 1. Se executor_name explícito e não é genérico municipal/nulo
                if (en && !en.includes('SEMUS') && !en.includes('SECRETARIA') && en !== 'NÃO INFORMADO' && en !== 'DESCONHECIDO' && en !== 'NONE' && en !== '-') {
                    return en;
                }

                // 2. Se executor_id aponta para um executor cadastrado não-SEMUS
                if (eidName && !eidName.includes('SEMUS') && !eidName.includes('SECRETARIA')) {
                    return eidName;
                }

                // 3. Resolução inteligente a partir de schedule_location
                if (loc) {
                    // Correspondência direta com executores cadastrados do município
                    for (const reName of registeredExecNames) {
                        if (loc.includes(reName) || (loc.length >= 4 && reName.includes(loc))) {
                            return reName;
                        }
                    }

                    // Mapeamento semântico padronizado da rede SUS/regulação
                    if (loc.includes('RUTH NOLETO') || loc.includes('MACRORREGIONAL')) {
                        return 'HMRN';
                    }
                    if (loc.includes('TOCANTINA') || loc.includes('HRT')) {
                        return 'HRT';
                    }
                    if (loc.includes('POLICLINICA') || loc.includes('POLICLÍNICA')) {
                        return 'POLICLINICA';
                    }
                    if (loc.includes('GRAJAU') || loc.includes('GRAJAÚ')) {
                        return 'HOSP DE GRAJAÚ';
                    }
                    if (loc.includes('VISUAL')) {
                        return 'VISUALMED';
                    }
                    if (loc.includes('CEUMA')) {
                        return 'CEUMA';
                    }
                    if (loc.includes('CEMI') || (loc.includes('IMPERATRIZ') && !loc.includes('POLICLINICA'))) {
                        return 'IMPERATRIZ';
                    }
                    if (loc.includes('SÃO JORGE') || loc.includes('SAO JORGE')) {
                        return 'HOSPITAL SÃO JORGE';
                    }
                    if (loc.includes('LAB MUN') || loc.includes('LABORAT')) {
                        return 'LAB MUN (SEMUSGEL)';
                    }
                    if (loc.includes('UBS') || loc.includes('USF') || loc.includes('POSTO')) {
                        return 'SEMUSGEL';
                    }

                    return loc;
                }

                return en || eidName || 'SEMUSGEL';
            };

            // 3. Se ano e mês foram passados, consolida números específicos do mês
            if (year && month) {
                const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
                const lastDay = new Date(year, month, 0).getDate();
                const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

                // Agendados no mês com seus respectivos executores e locais de atendimento
                const { data: mSched } = await supabase
                    .from('appointments')
                    .select('procedure_id, executor_name, executor_id, schedule_location')
                    .eq('municipality_id', municipalityId)
                    .eq('status', 'Agendado')
                    .gte('schedule_date', startDate)
                    .lte('schedule_date', endDate)
                    .neq('is_active', false)
                    .limit(5000);

                for (const a of mSched || []) {
                    const item = reportMap.get(a.procedure_id);
                    if (item) {
                        item.monthScheduledCount = (item.monthScheduledCount || 0) + 1;
                        const execName = resolveExecutorName(a);
                        if (!item.scheduledExecutorsMap) item.scheduledExecutorsMap = {};
                        item.scheduledExecutorsMap[execName] = (item.scheduledExecutorsMap[execName] || 0) + 1;
                    }
                }

                // Realizados no mês
                const { data: mDone } = await supabase
                    .from('appointments')
                    .select('procedure_id')
                    .eq('municipality_id', municipalityId)
                    .eq('status', 'Realizado')
                    .gte('outcome_date', startDate)
                    .lte('outcome_date', endDate)
                    .neq('is_active', false)
                    .limit(5000);

                for (const a of mDone || []) {
                    const item = reportMap.get(a.procedure_id);
                    if (item && item.monthDoneCount !== undefined) {
                        item.monthDoneCount++;
                    }
                }

                // Cancelados / Não Realizados no mês
                const { data: mCanc } = await supabase
                    .from('appointments')
                    .select('procedure_id')
                    .eq('municipality_id', municipalityId)
                    .in('status', ['Cancelado', 'Não Realizado'])
                    .gte('outcome_date', startDate)
                    .lte('outcome_date', endDate)
                    .neq('is_active', false)
                    .limit(5000);

                for (const a of mCanc || []) {
                    const item = reportMap.get(a.procedure_id);
                    if (item && item.monthCanceledCount !== undefined) {
                        item.monthCanceledCount++;
                    }
                }

                // Solicitações / Entradas no mês
                const { data: mReq } = await supabase
                    .from('appointments')
                    .select('procedure_id')
                    .eq('municipality_id', municipalityId)
                    .gte('registration_date', `${startDate}T00:00:00`)
                    .lte('registration_date', `${endDate}T23:59:59`)
                    .neq('is_active', false)
                    .limit(5000);

                for (const a of mReq || []) {
                    const item = reportMap.get(a.procedure_id);
                    if (item && item.monthRequestsCount !== undefined) {
                        item.monthRequestsCount++;
                    }
                }
            }

            // Formatação final dos campos calculados (Total do Mês e Texto dos Executores)
            for (const item of reportMap.values()) {
                if (item.scheduledExecutorsMap && Object.keys(item.scheduledExecutorsMap).length > 0) {
                    item.scheduledExecutors = Object.entries(item.scheduledExecutorsMap)
                        .sort((a, b) => b[1] - a[1])
                        .map(([exec, count]) => `${exec} (${count})`)
                        .join(', ');
                } else {
                    item.scheduledExecutors = (item.monthScheduledCount && item.monthScheduledCount > 0) ? 'NÃO ESPECIFICADO' : '-';
                }
                item.monthTotal = (item.monthScheduledCount || 0) + (item.monthDoneCount || 0) + (item.monthCanceledCount || 0);
            }

            return Array.from(reportMap.values());
        } catch (err: any) {
            console.error('[AppointmentService] Erro ao consolidar relatório de procedimentos:', err);
            this.toast.show('Erro ao carregar estatísticas de procedimentos: ' + err.message, 'error');
            return [];
        }
    }

    /**
     * Adiciona um novo agendamento.
     */
    async addAppointment(appointment: Appointment): Promise<Appointment> {
        try {
            const dbApp = this.mapAppointmentToDb(appointment);
            delete dbApp.id;

            const { data, error } = await supabase
                .from('appointments')
                .insert(dbApp)
                .select()
                .single();

            if (error) throw error;

            const newApp = this.mapDbToAppointment(data);
            this.appointmentsTable.update(list => [...list, newApp]);
            return newApp;
        } catch (err: any) {
            this.toast.show(err.message || 'Erro ao criar agendamento.', 'error');
            throw err;
        }
    }

    /**
     * Busca um único agendamento com todos os dados (incluindo imagens).
     */
    async getAppointmentById(id: number): Promise<Appointment | null> {
        try {
            const { data, error } = await supabase
                .from('appointments')
                .select('*')
                .eq('id', id)
                .maybeSingle();

            if (error || !data) {
                if (error) console.error('[AppointmentService] Erro ao buscar detalhes do agendamento:', error);
                return null;
            }
            return this.mapDbToAppointment(data);
        } catch (err) {
            console.error('[AppointmentService] Exceção ao buscar agendamento:', err);
            return null;
        }
    }

    /**
     * Busca sub-agendamentos (etapas de cirurgia) por ID pai.
     */
    async getAppointmentsByParentId(parentId: number): Promise<Appointment[]> {
        try {
            const { data, error } = await supabase
                .from('appointments')
                .select('*, procedures(name)')
                .eq('parent_appointment_id', parentId)
                .order('current_step_index', { ascending: true });

            if (error) {
                console.error('[AppointmentService] Erro ao buscar sub-agendamentos:', error);
                return [];
            }
            return (data || []).map(d => this.mapDbToAppointment(d));
        } catch (err) {
            console.error('[AppointmentService] Exceção ao buscar sub-agendamentos:', err);
            return [];
        }
    }

    async getRealQueueCount(procedureId: number, municipalityId: string, acsId: string | null): Promise<{ waiting: number; scheduling: number }> {
        try {
            let query;
            if (acsId) {
                query = supabase
                    .from('appointments')
                    .select('status, patients!inner(acs_id)')
                    .eq('procedure_id', procedureId)
                    .in('status', ['Aguardando', 'Em Agendamento'])
                    .eq('patients.acs_id', acsId);
            } else {
                query = supabase
                    .from('appointments')
                    .select('status')
                    .eq('procedure_id', procedureId)
                    .in('status', ['Aguardando', 'Em Agendamento'])
                    .eq('municipality_id', municipalityId);
            }

            const { data, error } = await query;
            if (error) throw error;

            const waiting = (data || []).filter(a => a.status === 'Aguardando').length;
            const scheduling = (data || []).filter(a => a.status === 'Em Agendamento').length;

            return { waiting, scheduling };
        } catch (err) {
            console.error('[AppointmentService] Erro ao contar fila real:', err);
            return { waiting: 0, scheduling: 0 };
        }
    }

    async countAcsAppointments(acsId: string, procedureId: number, monthRef: string): Promise<number> {
        // monthRef expects 'YYYY-MM'
        const startDate = `${monthRef}-01`;
        
        // Find actual end day (hardcoding 31 fails for months like April)
        const [y, m] = monthRef.split('-').map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        const endDate = `${monthRef}-${String(lastDay).padStart(2, '0')}`;

        const { count, error } = await supabase
            .from('appointments')
            .select('*', { count: 'exact', head: true })
            .eq('acs_id', acsId)
            .eq('procedure_id', procedureId)
            .neq('status', 'Cancelado')
            .gte('registration_date', startDate)
            .lte('registration_date', endDate);

        if (error) {
            console.error('[AppointmentService] error counting ACS appts:', error);
            return 0;
        }

        return count || 0;
    }

    async getAcsUsageStatus(acsId: string, procedureId: number): Promise<{ used: number, limit: number | null }> {
        const now = new Date();
        const monthRef = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        
        const used = await this.countAcsAppointments(acsId, procedureId, monthRef);
        
        // 1. Get ACS and its location_id
        const { data: acs } = await supabase
            .from('acs')
            .select('location_id, quotas')
            .eq('id', acsId)
            .maybeSingle();
            
        if (!acs) return { used, limit: null };
        
        // 2. Try to get UBS Weekly Pacing for this location
        let limit: number | null = null;
        if (acs.location_id) {
            const { data: ubs } = await supabase
                .from('procedure_locations')
                .select('daily_quotas')
                .eq('id', acs.location_id)
                .maybeSingle();
                
            const dailyVal = ubs?.daily_quotas?.[procedureId] || 0;
            if (dailyVal > 0) {
                const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
                limit = Math.floor(dailyVal * daysInMonth); // Total monthly limit based on daily quota
            }
        }

        // 3. Fallback to direct ACS quota if weekly pacing is not set
        if (limit === null) {
            limit = acs.quotas?.[procedureId] || null;
        }

        return { used, limit };
    }

    /**
     * Verifica se já existe um procedimento igual para o mesmo paciente nos últimos 90 dias (ou se já está na fila).
     */
    async checkDuplicateProcedure(
        patientId: number | null,
        cpf: string,
        procedureId: number,
        dateToCheck: string,
        excludeApptId?: number
    ): Promise<{ exists: boolean, message?: string }> {
        // 1. Resolver ID do Paciente se não fornecido
        let resolvedPatientId = patientId;
        if (!resolvedPatientId && this.dbService) {
            // Tenta limpar o CPF caso o banco armazene apenas números, ou formatado
            const cleanCpf = cpf.replace(/\D/g, '');
            const formattedCpf = cleanCpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');

            let pData = await this.dbService.findPatientByCpf(cleanCpf);
            if (!pData || !pData.patient) {
                pData = await this.dbService.findPatientByCpf(formattedCpf);
            }
            if (pData && pData.patient) resolvedPatientId = pData.patient.id;
        }

        if (!resolvedPatientId) {
            return { exists: false };
        }

        // 2. Query DB: Todos os agendamentos relevantes (além do dashboard atual)
        const { data: appointmentsRaw, error } = await supabase
            .from('appointments')
            .select('*')
            .eq('patient_id', resolvedPatientId);

        if (error) {
            return { exists: false };
        }

        const appointments = appointmentsRaw?.filter(a => Number(a.procedure_id) === Number(procedureId) && a.status !== 'Cancelado' && a.status !== 'Não Realizado') || [];

        if (appointments.length === 0) {
            return { exists: false };
        }

        // 3. Verificação de Lógica: Janela de 90 dias (ou paciente ativo na fila)
        let cleanDateToCheck = dateToCheck || new Date().toISOString();
        let targetTime: number;

        if (typeof cleanDateToCheck === 'string' && cleanDateToCheck.includes('-')) {
            const parts = cleanDateToCheck.split('T')[0].split('-').map(Number);
            if (parts.length >= 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
                targetTime = new Date(parts[0], parts[1] - 1, parts[2]).getTime();
            } else {
                targetTime = new Date(cleanDateToCheck).getTime();
            }
        } else {
            targetTime = new Date(cleanDateToCheck).getTime();
        }

        if (isNaN(targetTime)) {
            targetTime = new Date().getTime();
        }

        const duplicates = appointments.filter(a => {
            if (excludeApptId && a.id === excludeApptId) return false;

            // Se o paciente já está na fila (Aguardando ou Em Agendamento),
            // considera duplicidade independente da data.
            if (a.status === 'Aguardando' || a.status === 'Em Agendamento') {
                return true;
            }

            // Se o paciente já possui o procedimento com status 'Agendado',
            // considera duplicidade ativa para alertar o operador imediatamente.
            if (a.status === 'Agendado') {
                return true;
            }

            let compareStr: string;
            if (a.status === 'Realizado' && a.outcome_date) {
                compareStr = a.outcome_date;
            } else {
                compareStr = a.schedule_date || a.request_date || a.registration_date || new Date().toISOString();
            }

            let compareTime: number;
            if (typeof compareStr === 'string' && compareStr.includes('-')) {
                const parts = compareStr.split('T')[0].split('-').map(Number);
                if (parts.length >= 3 && !isNaN(parts[0]) && !isNaN(parts[1]) && !isNaN(parts[2])) {
                    compareTime = new Date(parts[0], parts[1] - 1, parts[2]).getTime();
                } else {
                    compareTime = new Date(compareStr).getTime();
                }
            } else {
                compareTime = new Date(compareStr).getTime();
            }

            if (isNaN(compareTime)) return false;

            // Calcula diferença em dias (janela de 90 dias)
            const diffMs = Math.abs(targetTime - compareTime);
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            return diffDays <= 90;
        });

        if (duplicates.length === 0) {
            return { exists: false };
        }

        if (duplicates.length > 0) {
            const dup = duplicates[0];
            let dateStr = dup.schedule_date || dup.outcome_date || dup.request_date ||
                (typeof dup.registration_date === 'string' ? dup.registration_date.split('T')[0] : new Date(dup.registration_date).toISOString().split('T')[0]);

            const dateParts = dateStr.split('-');
            const formattedDate = dateParts.length === 3 ? `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}` : dateStr;

            let suffix: string;
            if (dup.status === 'Aguardando' || dup.status === 'Em Agendamento') {
                suffix = `registrado em ${formattedDate}, portanto já está na fila`;
            } else if (dup.status === 'Agendado') {
                suffix = `agendado para ${formattedDate}`;
            } else {
                suffix = `no período de 90 dias (${formattedDate})`;
            }

            return {
                exists: true,
                message: `Este paciente já possui este procedimento com status '${dup.status}' ${suffix}.`
            };
        }

        return { exists: false };
    }

    /**
     * Busca agendamentos com realização pendente para um paciente específico (status 'Agendado' com data <= hoje).
     */
    async getPendingRealizationsForPatient(
        patientId: number | null,
        cpf?: string
    ): Promise<any[]> {
        let resolvedPatientId = patientId;
        let patientInfo: any = null;

        if (!resolvedPatientId && cpf && this.dbService) {
            const cleanCpf = cpf.replace(/\D/g, '');
            const formattedCpf = cleanCpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');

            let pData = await this.dbService.findPatientByCpf(cleanCpf);
            if (!pData || !pData.patient) {
                pData = await this.dbService.findPatientByCpf(formattedCpf);
            }
            if (pData && pData.patient) {
                resolvedPatientId = pData.patient.id;
                patientInfo = pData.patient;
            }
        }

        if (!resolvedPatientId) {
            return [];
        }

        if (!patientInfo && this.dbService) {
            patientInfo = this.dbService.patientsTable().find(p => p.id === resolvedPatientId);
        }

        // Buscar agendamentos do paciente no Supabase
        const { data: appointmentsRaw, error } = await supabase
            .from('appointments')
            .select('*')
            .eq('patient_id', resolvedPatientId)
            .eq('status', 'Agendado');

        if (error || !appointmentsRaw || appointmentsRaw.length === 0) {
            return [];
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const procs = this.dbService?.proceduresTable?.() || [];
        const procMap = new Map<number, any>(procs.map((p: any) => [p.id, p]));
        const executors = this.dbService?.executorsTable?.() || [];
        const execMap = new Map<string, any>(executors.map((e: any) => [String(e.id), e]));

        const pendingList: any[] = [];

        for (const raw of appointmentsRaw) {
            const app = this.mapDbToAppointment(raw);
            const scheduleDateStr = app.schedulingData?.date || raw.schedule_date;
            if (!scheduleDateStr) continue;

            const dateParts = String(scheduleDateStr).split('T')[0].split('-').map(Number);
            if (dateParts.length < 3 || isNaN(dateParts[0])) continue;

            const scheduledDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
            if (scheduledDate > today) continue; // Data futura -> não é realização pendente

            const proc = procMap.get(app.procedureId);
            const exec = app.executorId ? execMap.get(String(app.executorId)) : null;

            pendingList.push({
                ...app,
                procedureName: proc?.name || raw.procedure_name || 'Procedimento',
                category: proc?.category || 'CONSULTA',
                executorName: exec?.name || app.executorName || 'Local de Atendimento',
                patientName: patientInfo?.name || patientInfo?.nome || 'Paciente',
                patientSocialName: patientInfo?.socialName,
                patientCpf: patientInfo?.cpf || cpf || '',
                patientSusCard: patientInfo?.susCard || '---',
                patientPhone: patientInfo?.phone || ''
            });
        }

        return pendingList;
    }

    /**
     * Resolve um agendamento com realização pendente (Realizado, Aguardando ou Cancelado).
     */
    async resolvePendingRealization(
        appointmentId: number,
        outcome: 'Realizado' | 'Aguardando' | 'Cancelado',
        operatorId: number,
        outcomeLocation?: string
    ): Promise<Appointment> {
        let app = this.appointmentsTable().find(a => a.id === appointmentId);
        if (!app) {
            const { data: raw, error } = await supabase
                .from('appointments')
                .select('*')
                .eq('id', appointmentId)
                .single();
            if (error || !raw) throw new Error('Agendamento não localizado para atualização.');
            app = this.mapDbToAppointment(raw);
        }

        const todayStr = DateUtil.toBrasiliaDateString(new Date()) || new Date().toISOString().split('T')[0];

        const updatedApp: Appointment = {
            ...app,
            status: outcome,
            outcomeData: {
                date: todayStr,
                location: outcomeLocation || app.schedulingData?.location || 'Unidade Local',
                reason: outcome === 'Realizado' 
                    ? 'Confirmação ao registrar novo procedimento'
                    : (outcome === 'Aguardando' 
                        ? 'Retornado para a fila ao registrar novo procedimento (não realizado)' 
                        : 'Cancelado ao registrar novo procedimento (não realizado)')
            }
        };

        if (outcome === 'Aguardando') {
            // Limpa dados de agendamento pois voltou para a fila
            updatedApp.schedulingData = undefined;
            updatedApp.executorId = undefined;
        }

        await this.updateAppointment(updatedApp, operatorId, false);

        if (this.dbService) {
            const munId = this.dbService.currentMunicipalityId();
            if (munId) {
                const dashDate = this.dbService.dashboardDate();
                this.dbService.refreshDashboardStats(
                    munId,
                    dashDate.getFullYear(),
                    dashDate.getMonth() + 1
                );
            }
        }

        return updatedApp;
    }

    /**
     * Mapeia dados raw do banco para PatientProcess (Dashboard/Monitoramento).
     */
    mapToPatientProcess(p: any): PatientProcess {
        const db = this.dbService;
        const proc = (db?.proceduresTable() || []).find((pr: any) => pr.id === p.procedure_id || pr.id === p.procedureId);

        let regDate = new Date();
        if (p.registration_date) regDate = new Date(p.registration_date);
        else if (p.registrationDate) regDate = new Date(p.registrationDate);
        if (isNaN(regDate.getTime())) regDate = new Date();

        const reqDateStr = p.request_date || p.requestDate;
        let reqDate: Date | undefined = undefined;
        if (reqDateStr) {
            const d = new Date(reqDateStr);
            reqDate = isNaN(d.getTime()) ? undefined : d;
        }

        const today = new Date();
        const diffDays = Math.floor((today.getTime() - regDate.getTime()) / (1000 * 60 * 60 * 24));

        let daysSinceStatusChange = 0;
        if (p.status === 'Agendado' && p.schedule_date) {
            const sDate = new Date(p.schedule_date);
            daysSinceStatusChange = Math.ceil((sDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        } else if (p.status === 'Realizado' && p.outcome_date) {
            const oDate = new Date(p.outcome_date);
            daysSinceStatusChange = Math.floor((today.getTime() - oDate.getTime()) / (1000 * 60 * 60 * 24));
        }

        return {
            ...this.mapDbToAppointment(p),
            cpf: p.patient_cpf || p.cpf,
            name: p.patient_name || p.name,
            socialName: p.patient_social_name || p.social_name,
            susCard: p.susCard || p.patient_sus_card || p.sus_card,
            birthDate: p.birthDate || p.patient_birth_date || p.birth_date,
            phone: p.phone || p.patient_phone,
            address: {
                zip: p.address?.zip || p.patient_address_zip || p.patient_zip || p.address_zip || '',
                street: p.address?.street || p.patient_address_street || p.patient_street || p.address_street || '',
                number: p.address?.number || p.patient_address_number || p.patient_number || p.address_number || '',
                district: p.address?.district || p.patient_address_district || p.patient_district || p.address_district || '',
                city: p.address?.city || p.patient_address_city || p.patient_city || p.address_city || '',
                uf: p.address?.uf || p.patient_address_uf || p.patient_uf || p.address_uf || '',
            },
            procedureName: p.procedure_name || proc?.name || 'Desconhecido',
            category: (p.procedure_category || proc?.category || 'DESCONHECIDA').toUpperCase(),
            daysSinceRegistration: diffDays,
            daysSinceRequest: reqDate ? Math.floor((today.getTime() - reqDate.getTime()) / (1000 * 60 * 60 * 24)) : 0,
            daysSinceStatusChange,
            isExpired: diffDays > 120,
            isNearExpiration: diffDays > 90 && diffDays <= 120,
            isUrgency: p.urgency_priority || p.urgencyPriority || false,
            parentProcedureName: p.parent_procedure_name,
            residenceProofImage: p.residenceProofImage || p.patient_residence_proof_image || p.residence_proof_image_url,
            residenceProofDate: p.residenceProofDate || p.patient_residence_proof_date || p.residence_proof_date,
            requestDate: reqDate ? reqDate.toISOString() : undefined,
            registrationDate: regDate,
            requestImage: p.requestImage || p.request_image_url
        } as PatientProcess;
    }

    /**
     * Remove um agendamento.
     */
    async deleteAppointment(id: number) {
        try {
            this.loadingService.set(true);
            const { error } = await supabase.from('appointments').delete().eq('id', id);
            if (error) throw error;

            this.appointmentsTable.update(list => list.filter(a => a.id !== id));
            this.toast.show('Agendamento removido com sucesso.', 'success');
        } catch (err: any) {
            this.toast.show(err.message || 'Erro ao remover agendamento.', 'error');
            throw err;
        } finally {
            this.loadingService.set(false);
        }
    }

    /**
     * Mapeia dados do banco para o modelo Appointment.
     */
    mapDbToAppointment(a: any): Appointment {
        return {
            id: a.id,
            patientId: a.patient_id ?? a.patientId,
            procedureId: a.procedure_id ?? a.procedureId,
            municipalityId: a.municipality_id ?? a.municipalityId,
            originLocation: a.origin_location,
            anatomicalRegion: a.anatomical_region,
            priority: a.priority,
            status: a.status,
            registrationDate: new Date(a.registration_date),
            requestDate: a.request_date,
            requestImage: a.request_image_url,
            latestProtocol: a.latest_protocol,
            protocolHistory: a.protocol_history,
            judicialData: a.judicial_process_number ? {
                processNumber: a.judicial_process_number,
                origin: a.judicial_origin
            } : undefined,
            schedulingData: a.schedule_date ? {
                date: a.schedule_date,
                time: a.schedule_time,
                location: a.schedule_location
            } : undefined,
            outcomeData: (a.outcome_date || a.outcome_reason) ? {
                date: a.outcome_date,
                reason: a.outcome_reason,
                location: a.outcome_location
            } : undefined,
            preSurgeryCompleted: a.pre_surgery_completed,
            urgencyPriority: a.urgency_priority,
            urgencyJustification: typeof a.urgency_justification === 'string'
                ? { text: a.urgency_justification, justifiedBy: 'Sistema', justifiedAt: a.registration_date }
                : a.urgency_justification,
            auditTrail: a.audit_trail,
            parentAppointmentId: a.parent_appointment_id ?? a.parentAppointmentId,
            isSurgeryParent: a.is_surgery_parent ?? a.isSurgeryParent,
            subProtocol: a.sub_protocol ?? a.subProtocol,
            currentStepIndex: a.current_step_index ?? a.currentStepIndex,
            executorId: a.executor_id,
            executorName: a.executor_name,
            patientName: a.patient_name,
            patientCpf: a.patient_cpf,
            patientSocialName: a.patient_social_name,
            patientPhone: a.patient_phone,
            procedureName: a.procedure_name || a.procedures?.name,
            category: a.procedure_category,
            acsId: a.acs_id,
            acsName: a.acs_name,
            selectedSubExams: a.selected_sub_exams || undefined,  // NEW: Lista de subexames do Exame de Grupo
            allocatedProfessionalId: a.allocated_professional_id,
            allocatedDate: a.allocated_date,
            allocatedTime: a.allocated_time,
            schedulingStartedAt: a.scheduling_started_at
        };
    }

    /**
     * Mapeia o modelo Appointment para o formato do banco.
     */
    mapAppointmentToDb(a: Appointment): any {
        return {
            patient_id: a.patientId,
            procedure_id: a.procedureId,
            municipality_id: a.municipalityId,
            origin_location: a.originLocation,
            anatomical_region: a.anatomicalRegion,
            priority: a.priority,
            status: a.status,
            // Garante ISO timestamp completo para TIMESTAMPTZ
            registration_date: AppointmentService.toDbTimestamp(a.registrationDate),
            // Normaliza para YYYY-MM-DD (tipo DATE no Postgres)
            request_date: AppointmentService.normalizeDate(a.requestDate),
            request_image_url: a.requestImage,
            latest_protocol: a.latestProtocol,
            protocol_history: a.protocolHistory,
            judicial_process_number: a.judicialData?.processNumber,
            judicial_origin: a.judicialData?.origin,
            schedule_date: AppointmentService.normalizeDate(a.schedulingData?.date),
            schedule_time: a.schedulingData?.time,
            schedule_location: a.schedulingData?.location,
            outcome_date: AppointmentService.normalizeDate(a.outcomeData?.date),
            outcome_reason: a.outcomeData?.reason,
            outcome_location: a.outcomeData?.location,
            pre_surgery_completed: a.preSurgeryCompleted,
            urgency_priority: a.urgencyPriority,
            urgency_justification: a.urgencyJustification,
            audit_trail: a.auditTrail,
            parent_appointment_id: a.parentAppointmentId,
            is_surgery_parent: a.isSurgeryParent,
            sub_protocol: a.subProtocol,
            current_step_index: a.currentStepIndex,
            executor_id: a.executorId,
            executor_name: a.executorName,
            acs_id: a.acsId,
            selected_sub_exams: a.selectedSubExams || null,  // NEW: Persiste lista de subexames selecionados (JSONB)
            allocated_professional_id: a.allocatedProfessionalId,
            allocated_date: a.allocatedDate,
            allocated_time: a.allocatedTime,
            scheduling_started_at: a.schedulingStartedAt ? AppointmentService.toDbTimestamp(a.schedulingStartedAt) : undefined
        };
    }

    /**
     * Atualiza o status de um agendamento com auditoria detalhada e gatilhos de sistema.
     */
    async updateAppointment(
        updatedApp: Appointment,
        operatorId: number,
        triggerWebhook: boolean = false,
        options?: { hideLoading?: boolean; silentToast?: boolean }
    ) {
        const hideLoading = options?.hideLoading ?? false;
        const silentToast = options?.silentToast ?? false;
        try {
            if (!hideLoading) this.loadingService.set(true);
            let oldApp = this.appointmentsTable().find(a => a.id === updatedApp.id);

            // Blindagem: se parentAppointmentId ou isSurgeryParent não vieram, ou se oldApp não está na memória, recupera do BD
            if (!oldApp || (!updatedApp.parentAppointmentId && !updatedApp.isSurgeryParent)) {
                const { data: dbCheck } = await supabase
                    .from('appointments')
                    .select('id, status, priority, urgency_justification, schedule_date, schedule_time, schedule_location, executor_id, outcome_reason, latest_protocol, parent_appointment_id, is_surgery_parent, current_step_index, pre_surgery_completed, procedure_id, patient_id, municipality_id')
                    .eq('id', updatedApp.id)
                    .maybeSingle();

                if (dbCheck) {
                    if (!updatedApp.parentAppointmentId && dbCheck.parent_appointment_id) {
                        updatedApp.parentAppointmentId = dbCheck.parent_appointment_id;
                    }
                    if (updatedApp.isSurgeryParent === undefined && dbCheck.is_surgery_parent !== null && dbCheck.is_surgery_parent !== undefined) {
                        updatedApp.isSurgeryParent = dbCheck.is_surgery_parent;
                    }
                    if (updatedApp.currentStepIndex === undefined && dbCheck.current_step_index !== null && dbCheck.current_step_index !== undefined) {
                        updatedApp.currentStepIndex = dbCheck.current_step_index;
                    }
                    if (!oldApp) {
                        oldApp = {
                            id: dbCheck.id,
                            status: dbCheck.status,
                            priority: dbCheck.priority,
                            latestProtocol: dbCheck.latest_protocol,
                            parentAppointmentId: dbCheck.parent_appointment_id,
                            isSurgeryParent: dbCheck.is_surgery_parent,
                            currentStepIndex: dbCheck.current_step_index,
                            preSurgeryCompleted: dbCheck.pre_surgery_completed,
                            procedureId: dbCheck.procedure_id,
                            patientId: dbCheck.patient_id,
                            municipalityId: dbCheck.municipality_id,
                            schedulingData: dbCheck.schedule_date ? {
                                date: dbCheck.schedule_date,
                                time: dbCheck.schedule_time,
                                location: dbCheck.schedule_location
                            } : undefined
                        } as any;
                    }
                }
            }

            const changes: any[] = [];

            if (oldApp) {
                // Preserva os contadores reais de etapas para impedir que formulários demográficos desatualizados do frontend sobrescrevam o progresso
                updatedApp.preSurgeryCompleted = oldApp.preSurgeryCompleted;
                if (updatedApp.currentStepIndex === undefined) {
                    updatedApp.currentStepIndex = oldApp.currentStepIndex;
                }
                if (updatedApp.parentAppointmentId === undefined && oldApp.parentAppointmentId) {
                    updatedApp.parentAppointmentId = oldApp.parentAppointmentId;
                }
                if (updatedApp.isSurgeryParent === undefined && oldApp.isSurgeryParent !== undefined) {
                    updatedApp.isSurgeryParent = oldApp.isSurgeryParent;
                }

                if (oldApp.status !== updatedApp.status) changes.push({ field: 'Status', old: oldApp.status, new: updatedApp.status });
                if (oldApp.priority !== updatedApp.priority) changes.push({ field: 'Prioridade', old: oldApp.priority, new: updatedApp.priority });
                if (oldApp.urgencyJustification?.text !== updatedApp.urgencyJustification?.text) {
                    changes.push({ field: 'Justificativa Urgência', old: oldApp.urgencyJustification?.text, new: updatedApp.urgencyJustification?.text });
                }
                if (oldApp.schedulingData?.date !== updatedApp.schedulingData?.date) changes.push({ field: 'Data Agendamento', old: oldApp.schedulingData?.date, new: updatedApp.schedulingData?.date });
                if (oldApp.schedulingData?.time !== updatedApp.schedulingData?.time) changes.push({ field: 'Horário', old: oldApp.schedulingData?.time, new: updatedApp.schedulingData?.time });
                if (oldApp.schedulingData?.location !== updatedApp.schedulingData?.location) changes.push({ field: 'Local', old: oldApp.schedulingData?.location, new: updatedApp.schedulingData?.location });
                if (oldApp.executorId !== updatedApp.executorId) changes.push({ field: 'Executor', old: oldApp.executorId, new: updatedApp.executorId });
                if (updatedApp.outcomeData?.reason && oldApp.outcomeData?.reason !== updatedApp.outcomeData.reason) {
                    changes.push({ field: 'Motivo', old: oldApp.outcomeData?.reason, new: updatedApp.outcomeData.reason });
                }
            }

            let protocoloReferencia = updatedApp.latestProtocol;

            // Recobrar protocolo se estiver faltando
            if (!protocoloReferencia && oldApp?.latestProtocol) {
                protocoloReferencia = oldApp.latestProtocol;
                updatedApp.latestProtocol = protocoloReferencia;
            }

            // Se ainda não possuir protocolo (ex: criado durante período sem sequencial), gera agora
            if (!protocoloReferencia) {
                try {
                    protocoloReferencia = await this.auditService.gerarProtocolo(
                        'ATENDIMENTO_INICIAL' as any,
                        'AGENDAMENTO',
                        updatedApp.id,
                        operatorId,
                        undefined,
                        undefined,
                        `Atribuição de protocolo retroativo para agendamento #${updatedApp.id}`
                    );
                    updatedApp.latestProtocol = protocoloReferencia;
                } catch (pErr) {
                    console.warn('[AppointmentService] Aviso ao gerar protocolo na atualização do agendamento:', pErr);
                }
            }

            const wasScheduledBefore = oldApp ? (oldApp.status === 'Agendado' || Boolean(oldApp.schedulingData?.date || (oldApp as any).schedule_date)) : Boolean(updatedApp.schedulingData?.date);

            // Se for marcado como Realizado mas NÃO estava agendado previamente,
            // alinha schedule_date e schedule_location com os dados de realização
            // para que triggers de cota no banco e filtros operem no mês de realização selecionado
            if (updatedApp.status === 'Realizado' && !wasScheduledBefore && updatedApp.outcomeData?.date) {
                if (!updatedApp.schedulingData?.date) {
                    updatedApp.schedulingData = {
                        date: updatedApp.outcomeData.date,
                        time: '08:00',
                        location: updatedApp.outcomeData.location || 'Unidade Local'
                    };
                }
            }

            const { error } = await supabase
                .from('appointments')
                .update(this.mapAppointmentToDb(updatedApp))
                .eq('id', updatedApp.id);

            if (error) throw error;

            if (protocoloReferencia) {
                const patient = this.dbService?.patientsTable?.().find((p: any) => p.id === updatedApp.patientId);
                const procedure = this.dbService?.proceduresTable?.().find((p: any) => p.id === updatedApp.procedureId);
                const executor = this.dbService?.executorsTable?.().find((e: any) => e.id === updatedApp.executorId);
                
                const dadosAnteriores = oldApp ? this.getOptimizedAuditData(oldApp, patient, procedure, executor) : null;
                const dadosNovos = this.getOptimizedAuditData(updatedApp, patient, procedure, executor);
                
                const statusChange = changes.find(c => c.field === 'Status');
                const tipoAcao = statusChange ? 'MUDANCA_STATUS' : 'EDICAO_DADOS';
                const changeDescription = changes.length > 0 
                    ? changes.map(c => `${c.field}: ${c.old || '(vazio)'} -> ${c.new || '(vazio)'}`).join('; ')
                    : 'Dados visualizados/confirmados';
                const reasonSuffix = (updatedApp.outcomeData?.reason && !changeDescription.includes('Motivo:')) ? ` (Motivo: ${updatedApp.outcomeData.reason})` : '';
                
                await this.auditService.registrarAuditoria(
                    protocoloReferencia,
                    tipoAcao,
                    tipoAcao === 'MUDANCA_STATUS' 
                        ? `Alteração de status do atendimento. ${changeDescription}${reasonSuffix}`
                        : `Edição de dados do agendamento. ${changeDescription}${reasonSuffix}`,
                    operatorId,
                    undefined,
                    dadosAnteriores,
                    dadosNovos
                );
            }

            this.appointmentsTable.update(list => list.map(a => a.id === updatedApp.id ? updatedApp : a));

            // Gatilho de Webhook N8N (apenas se solicitado e status for Agendado)
            // Agora assíncrono para NÃO restringir o sistema (o operador pode continuar trabalhando)
            if (triggerWebhook && updatedApp.status === 'Agendado') {
                if (!silentToast) this.toast.show('Entrando em contato com o paciente...', 'info');
                this.triggerN8NWebhook(updatedApp).then(webhookResult => {
                    if (webhookResult.pending) {
                        // Toast removido conforme solicitação para simplificar a UI
                    } else if (!webhookResult.success) {
                        if (!silentToast) this.toast.show('Mensagem não enviada para o paciente, aguarde a nova tentativa automática', 'warning');
                    } else {
                        if (!silentToast) this.toast.show('Mensagem enviada com sucesso para o paciente.', 'success');
                    }
                });
            }

            // Reflexo na cota do mês selecionado quando o procedimento foi realizado sem agendamento prévio
            if (updatedApp.status === 'Realizado' && !wasScheduledBefore && updatedApp.outcomeData?.date && updatedApp.municipalityId && updatedApp.procedureId) {
                try {
                    await this.registerDirectRealizationQuota(
                        updatedApp.id,
                        updatedApp.procedureId,
                        updatedApp.municipalityId,
                        updatedApp.patientId,
                        updatedApp.outcomeData.date,
                        updatedApp.outcomeData.location || 'Unidade Local'
                    );
                } catch (quotaErr) {
                    console.warn('[AppointmentService] Aviso ao sincronizar cota para realização direta:', quotaErr);
                }
            }

            // Progressão ou Regressão Automática de Cirurgia
            const isCompleted = updatedApp.status === 'Realizado' || updatedApp.status === 'Dispensado';
            const wasCompleted = oldApp ? (oldApp.status === 'Realizado' || oldApp.status === 'Dispensado') : false;

            if (updatedApp.parentAppointmentId) {
                // É uma sub-etapa
                if (isCompleted && !wasCompleted) {
                    await this.handleSurgeryProgression(updatedApp, operatorId);
                } else if (!isCompleted && wasCompleted) {
                    // Reversão de uma etapa já concluída
                    await this.handleSurgeryRegression(updatedApp, operatorId);
                } else if (updatedApp.status === 'Cancelado' && !wasCompleted) {
                    // Cancelamento de uma etapa ativa (Aguardando/Agendado)
                    // O ciclo ficaria preso sem uma etapa ativa, então recriamos a etapa
                    await this.handleSurgeryRegression(updatedApp, operatorId);
                } else if (isCompleted && !oldApp) {
                    // Fallback caso não exista oldApp
                    await this.handleSurgeryProgression(updatedApp, operatorId);
                }
            } else if (updatedApp.isSurgeryParent || (!updatedApp.parentAppointmentId && this.dbService?.proceduresTable?.().find((p: any) => p.id === updatedApp.procedureId)?.requiresPreSurgery)) {
                // É a cirurgia PAI
                if (isCompleted && !wasCompleted) {
                    const proc = this.dbService?.proceduresTable?.().find((p: any) => p.id === updatedApp.procedureId);
                    const stepsConfig = await this.getSurgerySteps(updatedApp.procedureId);
                    const totalSteps = stepsConfig.length > 0 ? stepsConfig.length : (proc?.preSurgeryCount || 0);
                    
                    if (totalSteps > 0 && updatedApp.preSurgeryCompleted !== totalSteps) {
                        updatedApp.preSurgeryCompleted = totalSteps;
                        await supabase.from('appointments').update({ pre_surgery_completed: totalSteps }).eq('id', updatedApp.id);
                        
                        await supabase
                            .from('appointments')
                            .update({ status: 'Dispensado', outcome_reason: 'Cirurgia principal concluída diretamente.' })
                            .eq('parent_appointment_id', updatedApp.id)
                            .neq('status', 'Realizado')
                            .neq('status', 'Cancelado')
                            .neq('status', 'Dispensado');

                        this.appointmentsTable.update(list => list.map(a => 
                            (a.parentAppointmentId === updatedApp.id && a.status !== 'Realizado' && a.status !== 'Cancelado' && a.status !== 'Dispensado')
                                ? { ...a, status: 'Dispensado' as const, outcomeData: { date: new Date().toISOString().split('T')[0], reason: 'Cirurgia principal concluída diretamente.' } }
                                : (a.id === updatedApp.id ? { ...a, preSurgeryCompleted: totalSteps } : a)
                        ));
                    }
                }
            }

            if (!silentToast) {
                this.toast.show('Agendamento concluído com sucesso.', 'success');
            }
            return protocoloReferencia;
        } catch (err: any) {
            console.error('[AppointmentService] Erro ao atualizar agendamento:', err);
            if (!silentToast) {
                this.toast.show(err.message || 'Erro ao atualizar agendamento.', 'error');
            }
            throw err;
        } finally {
            if (!hideLoading) this.loadingService.set(false);
        }
    }

    private async handleSurgeryProgression(updatedApp: Appointment, operatorId: number) {
        let currentStep = Number(updatedApp.currentStepIndex);

        // Recuperar currentStepIndex do banco caso venha nulo do frontend (fallback de segurança)
        if (isNaN(currentStep) || currentStep === undefined || currentStep === null) {
            const { data: dbApp } = await supabase.from('appointments').select('current_step_index').eq('id', updatedApp.id).maybeSingle();
            currentStep = Number(dbApp?.current_step_index ?? 0);
            console.warn(`[AppointmentService] currentStepIndex estava ausente. Recuperado do banco: ${currentStep}`);
        }

        const completedCount = currentStep + 1;

        // Recuperar o pai do banco diretamente para ter certeza do estado atual vs estado em memória
        const { data: dbParent } = await supabase.from('appointments').select('pre_surgery_completed').eq('id', updatedApp.parentAppointmentId).single();
        const currentCompleted = Number(dbParent?.pre_surgery_completed || 0);

        // Proteção contra progressão duplicada (por exemplo, clicou "Salvar" duas vezes ou trigger disparou novamente)
        if (currentCompleted >= completedCount) {
            console.log(`[AppointmentService] Progressão ignorada: pai já registrou ${currentCompleted} etapas concluídas (tentando processar etapa ${completedCount}).`);
            return;
        }

        const actionLabel = updatedApp.status === 'Dispensado' ? 'dispensada' : 'concluída';
        console.log(`[AppointmentService] handleSurgeryProgression DISPARADO (${actionLabel}): Parent=${updatedApp.parentAppointmentId}, CurrentStep=${currentStep}, Completadas no BD=${currentCompleted}`);

        // Atualizar pai no banco
        await supabase.from('appointments').update({ pre_surgery_completed: completedCount }).eq('id', updatedApp.parentAppointmentId);

        // Atualizar estado local do pai
        this.appointmentsTable.update(list => list.map(a =>
            a.id === updatedApp.parentAppointmentId ? { ...a, preSurgeryCompleted: completedCount } : a
        ));

        const nextProtocol = await this.createNextSurgeryStep(updatedApp.parentAppointmentId!, currentStep + 1, operatorId);

        if (!nextProtocol) {
            // Ciclo concluído
            await supabase.from('appointments').update({
                status: 'Aguardando',
                pre_surgery_completed: completedCount
            }).eq('id', updatedApp.parentAppointmentId);

            if (updatedApp.latestProtocol) {
                const parentApp = this.dbService?.appointmentsTable?.().find((a: any) => a.id === updatedApp.parentAppointmentId);
                const patient = parentApp ? this.dbService?.patientsTable?.().find((p: any) => p.id === parentApp.patientId) : null;
                const procedure = this.dbService?.proceduresTable?.().find((p: any) => p.id === updatedApp.procedureId);
                
                const dadosNovos = this.getOptimizedAuditData(updatedApp, patient, procedure);
                
                await this.auditService.registrarAuditoria(
                    updatedApp.latestProtocol,
                    'MUDANCA_STATUS',
                    `Ciclo cirúrgico concluído (${completedCount} etapas finalizadas). Paciente apto para agendamento da cirurgia principal.`,
                    operatorId,
                    null,
                    null,
                    dadosNovos
                );
            }

            this.appointmentsTable.update(list => list.map(a =>
                a.id === updatedApp.parentAppointmentId
                    ? { ...a, status: 'Aguardando' as const, preSurgeryCompleted: completedCount }
                    : a
            ));
        }
    }

    private async handleSurgeryRegression(cancelledApp: Appointment, operatorId: number) {
        let stepIndex = Number(cancelledApp.currentStepIndex);

        // Recuperar currentStepIndex do banco caso venha nulo
        if (isNaN(stepIndex) || stepIndex === undefined || stepIndex === null) {
            const { data: dbApp } = await supabase.from('appointments').select('current_step_index').eq('id', cancelledApp.id).maybeSingle();
            stepIndex = Number(dbApp?.current_step_index ?? 0);
        }

        console.log(`[AppointmentService] handleSurgeryRegression DISPARADO: Parent=${cancelledApp.parentAppointmentId}, StepIndex=${stepIndex}, Status=${cancelledApp.status}`);

        // 1. Atualizar o pai para voltar o pre_surgery_completed e current_step_index para o índice desta etapa
        await supabase.from('appointments').update({
            pre_surgery_completed: stepIndex,
            current_step_index: stepIndex
        }).eq('id', cancelledApp.parentAppointmentId);

        // Atualizar estado local do pai
        this.appointmentsTable.update(list => list.map(a =>
            a.id === cancelledApp.parentAppointmentId ? { ...a, preSurgeryCompleted: stepIndex, currentStepIndex: stepIndex } : a
        ));

        // 2. Cancelar todas as etapas subsequentes que já foram criadas
        await supabase.from('appointments')
            .update({ status: 'Cancelado', outcome_reason: 'Etapa anterior foi revertida/cancelada.' })
            .eq('parent_appointment_id', cancelledApp.parentAppointmentId)
            .gt('current_step_index', stepIndex)
            .neq('status', 'Cancelado');

        // Atualizar localmente as etapas subsequentes
        this.appointmentsTable.update(list => list.map(a =>
            (a.parentAppointmentId === cancelledApp.parentAppointmentId && (a.currentStepIndex ?? 0) > stepIndex && a.status !== 'Cancelado')
                ? { ...a, status: 'Cancelado' as const, outcomeData: { date: new Date().toISOString().split('T')[0], reason: 'Etapa anterior foi revertida/cancelada.' } }
                : a
        ));

        // 3. Se a etapa atual foi cancelada (e não apenas revertida para Aguardando), recriamos ela para que o ciclo não trave
        if (cancelledApp.status === 'Cancelado') {
            await this.createNextSurgeryStep(cancelledApp.parentAppointmentId!, stepIndex, operatorId);
        }
    }

    /**
     * Pula (dispensa) uma etapa do ciclo pré-operatório e avança automaticamente para a próxima.
     */
    async skipSurgeryStep(stepAppointmentId: number, operatorId: number): Promise<void> {
        const { data: raw, error } = await supabase
            .from('appointments')
            .select('*')
            .eq('id', stepAppointmentId)
            .single();

        if (error || !raw) throw new Error('Etapa não encontrada.');

        const step = this.mapDbToAppointment(raw);
        if (!step.parentAppointmentId) throw new Error('Esta etapa não está vinculada a um ciclo cirúrgico.');

        const todayStr = new Date().toISOString().split('T')[0];

        // Marca a etapa como Dispensada
        const { error: updErr } = await supabase
            .from('appointments')
            .update({ 
                status: 'Dispensado',
                outcome_date: todayStr,
                outcome_reason: 'Etapa dispensada pelo regulador.'
            })
            .eq('id', stepAppointmentId);

        if (updErr) throw updErr;

        // Atualiza estado local
        this.appointmentsTable.update(list =>
            list.map(a => a.id === stepAppointmentId ? { 
                ...a, 
                status: 'Dispensado' as const,
                outcomeData: {
                    date: todayStr,
                    reason: 'Etapa dispensada pelo regulador.'
                }
            } : a)
        );

        // Auditoria
        const targetProtocol = step.latestProtocol || step.subProtocol;
        if (targetProtocol) {
            await this.auditService.registrarAuditoria(
                targetProtocol,
                'MUDANCA_STATUS' as any,
                `Etapa ${(step.currentStepIndex ?? 0) + 1} dispensada pelo regulador.`,
                operatorId
            );
        }

        // Dispara progressão automática (mesma lógica do Realizado)
        await this.handleSurgeryProgression({ ...step, status: 'Dispensado' as const }, operatorId);
    }

    /**
     * Confirma a realização de uma etapa do ciclo pré-operatório e avança automaticamente para a próxima.
     */
    async completeSurgeryStep(
        stepAppointmentId: number, 
        operatorId: number, 
        outcomeDate?: string, 
        outcomeLocation?: string
    ): Promise<void> {
        const { data: raw, error } = await supabase
            .from('appointments')
            .select('*')
            .eq('id', stepAppointmentId)
            .single();

        if (error || !raw) throw new Error('Etapa não encontrada.');

        const step = this.mapDbToAppointment(raw);
        if (!step.parentAppointmentId) throw new Error('Esta etapa não está vinculada a um ciclo cirúrgico.');

        const todayStr = DateUtil.toBrasiliaDateString(new Date()) || new Date().toISOString().split('T')[0];
        const effectiveDate = outcomeDate || todayStr;
        const effectiveLocation = outcomeLocation || step.schedulingData?.location || 'Unidade de Atendimento';
        const wasScheduled = step.status === 'Agendado' || Boolean(step.schedulingData?.date || raw.schedule_date);

        const updatePayload: any = { 
            status: 'Realizado',
            outcome_date: effectiveDate,
            outcome_reason: 'Etapa concluída pelo regulador.',
            outcome_location: effectiveLocation
        };

        // Se não estava agendado previamente, preenche schedule_date e schedule_location
        // para refletir diretamente na cota daquele mês no banco e na UI
        if (!wasScheduled) {
            updatePayload.schedule_date = effectiveDate;
            updatePayload.schedule_location = effectiveLocation;
        }

        // Marca a etapa como Realizada no banco
        const { error: updErr } = await supabase
            .from('appointments')
            .update(updatePayload)
            .eq('id', stepAppointmentId);

        if (updErr) throw updErr;

        // Atualiza estado local na tabela de appointments
        this.appointmentsTable.update(list =>
            list.map(a => a.id === stepAppointmentId ? { 
                ...a, 
                status: 'Realizado' as const,
                ...(wasScheduled ? {} : {
                    schedulingData: {
                        date: effectiveDate,
                        time: '08:00',
                        location: effectiveLocation
                    }
                }),
                outcomeData: {
                    date: effectiveDate,
                    reason: 'Etapa concluída pelo regulador.',
                    location: effectiveLocation
                }
            } : a)
        );

        // Se foi realização direta sem agendamento prévio, garante o registro na cota do mês selecionado
        if (!wasScheduled && step.municipalityId && step.procedureId) {
            try {
                await this.registerDirectRealizationQuota(
                    step.id,
                    step.procedureId,
                    step.municipalityId,
                    step.patientId,
                    effectiveDate,
                    effectiveLocation
                );
            } catch (qErr) {
                console.warn('[AppointmentService] Aviso ao computar cota de realização direta em sub-etapa:', qErr);
            }
        }

        // Auditoria
        const targetProtocol = step.latestProtocol || step.subProtocol;
        if (targetProtocol) {
            await this.auditService.registrarAuditoria(
                targetProtocol,
                'CONFIRMACAO_REALIZACAO' as any,
                `Etapa ${(step.currentStepIndex ?? 0) + 1} concluída pelo regulador (Realizada em ${effectiveDate} no local ${effectiveLocation}).`,
                operatorId
            );
        }

        // Dispara progressão automática para a próxima etapa do ciclo cirúrgico
        await this.handleSurgeryProgression({ 
            ...step, 
            status: 'Realizado' as const,
            schedulingData: wasScheduled ? step.schedulingData : { date: effectiveDate, time: '08:00', location: effectiveLocation },
            outcomeData: { date: effectiveDate, location: effectiveLocation, reason: 'Etapa concluída pelo regulador.' }
        }, operatorId);

        // Sincroniza estado de negócio e estatísticas de cota do mês correspondente
        if (step.municipalityId) {
            this.dbService?.syncBusinessState?.(step.municipalityId);
            const [yStr, mStr] = effectiveDate.split('-');
            const y = parseInt(yStr, 10);
            const m = parseInt(mStr, 10);
            if (!isNaN(y) && !isNaN(m)) {
                this.dbService?.refreshDashboardStats?.(step.municipalityId, y, m);
            }
        }
    }

    /**
     * Garante que uma realização direta (sem agendamento prévio) seja refletida
     * na cota do procedimento no mês em que foi realizada.
     */
    async registerDirectRealizationQuota(
        appointmentId: number,
        procedureId: number,
        municipalityId: string,
        patientId: number,
        outcomeDate: string,
        outcomeLocation: string
    ): Promise<void> {
        const monthRef = outcomeDate.substring(0, 7) + '-01';

        // 1. Procura pool para este procedimento no mês selecionado
        const { data: pool } = await supabase
            .from('quota_pools')
            .select('id, total_authorized')
            .eq('procedure_id', procedureId)
            .eq('municipality_id', municipalityId)
            .eq('month_ref', monthRef)
            .maybeSingle();

        const targetPoolId = pool?.id || null;

        // 2. Tenta preencher um slot disponível desse pool ou cria um ad-hoc
        let slotFilled = false;
        if (targetPoolId) {
            const { data: availableSlot } = await supabase
                .from('agenda_slots')
                .select('id')
                .eq('pool_id', targetPoolId)
                .eq('is_available', true)
                .order('start_time', { ascending: true, nullsFirst: false })
                .limit(1)
                .maybeSingle();

            if (availableSlot) {
                await supabase
                    .from('agenda_slots')
                    .update({
                        patient_id: patientId,
                        appointment_id: appointmentId,
                        location_name: outcomeLocation,
                        start_time: `${outcomeDate}T08:00:00`,
                        is_available: false
                    })
                    .eq('id', availableSlot.id);
                slotFilled = true;
            }
        }

        // Se não havia slot disponível para preencher, cria um slot preenchido vinculado ao pool (ou avulso no mês)
        if (!slotFilled) {
            await supabase
                .from('agenda_slots')
                .insert({
                    pool_id: targetPoolId,
                    municipality_id: municipalityId,
                    location_name: outcomeLocation,
                    start_time: `${outcomeDate}T08:00:00`,
                    end_time: `${outcomeDate}T08:00:00`,
                    patient_id: patientId,
                    appointment_id: appointmentId,
                    is_available: false,
                    is_active: true
                });
        }

        // 3. Notifica / atualiza caches de quota
        if (this.dbService?.quotaService) {
            const targetMonthShort = outcomeDate.substring(0, 7);
            await this.dbService.quotaService.refreshQuotaStats(municipalityId, targetMonthShort);
            await this.dbService.quotaService.refreshQuotaPools(municipalityId, targetMonthShort);
        }
    }

    /**
     * Reenvia o payload de agendamento para o webhook N8N configurado.
     * Método público para uso on-demand (ex: botão "Reenviar Mensagem").
     */
    async resendAppointmentWebhook(appointment: Appointment): Promise<void> {
        const res = await this.triggerN8NWebhook(appointment);
        if (!res.success) {
            throw new Error('Envio não realizado: Notificações desativadas para este município ou telefone fixo/inválido.');
        }
    }

    public async triggerN8NWebhook(appointment: Appointment): Promise<{ success: boolean; pending: boolean }> {
        try {
            // 0. Obter configurações do município
            const { data: currentMun, error: munError } = await supabase
                .from('municipalities')
                .select('*')
                .eq('id', appointment.municipalityId)
                .single();

            if (munError || !currentMun || !currentMun.enable_n8n || !currentMun.n8n_webhook_url) {
                console.warn('[Webhook] n8n desativado ou sem URL base para este município.');
                return { success: false, pending: false };
            }

            // 1. Resolução da URL por Hierarquia
            let resolvedWebhookUrl = currentMun.n8n_webhook_url;

            // Lógica de Roteamento Hierárquico
            // UBS de Origem > Local de Execução > Webhook Central do Município
            if (currentMun.enable_ubs_module) {
                // Módulo UBS Ativo -> Prioridade 1: Unidade de Origem (Quem solicitou)
                // Prioridade 2: Local de Execução (Onde será atendido)
                const locationName = appointment.originLocation || appointment.schedulingData?.location;

                if (locationName && locationName !== 'Não informado') {
                    const { data: locData } = await supabase.from('procedure_locations')
                        .select('n8n_webhook_url')
                        .eq('name', locationName)
                        .eq('municipality_id', appointment.municipalityId)
                        .maybeSingle();

                    if (locData?.n8n_webhook_url) {
                        resolvedWebhookUrl = locData.n8n_webhook_url;
                        console.log(`[Webhook Resolution] UBS Ativo. Usando webhook da Unidade: ${locationName} -> ${resolvedWebhookUrl}`);
                    }
                }
            } else {
                console.log(`[Webhook Resolution] UBS Inativo. Usando webhook central do Município.`);
            }

            // Dados para o payload e substituição
            // Buscar dados frescos do paciente diretamente do banco
            let patientName = 'Paciente';
            let patientPhone = '';
            if (appointment.patientId) {
                const { data: freshPatient } = await supabase
                    .from('patients')
                    .select('name, phone')
                    .eq('id', appointment.patientId)
                    .maybeSingle();
                if (freshPatient) {
                    patientName = freshPatient.name || appointment.patientName || 'Paciente';
                    patientPhone = freshPatient.phone || appointment.patientPhone || '';
                } else {
                    patientName = appointment.patientName || 'Paciente';
                    patientPhone = appointment.patientPhone || '';
                }
            } else {
                patientName = appointment.patientName || 'Paciente';
                patientPhone = appointment.patientPhone || '';
            }

            // Validação e higienização de telefone (Proteção Anti-Ban: Evita bounce rate no WhatsApp por números fixos/inválidos)
            let isPotentialWhatsApp = false;
            let phoneRejectionReason = '';

            if (patientPhone) {
                patientPhone = patientPhone.replace(/\D/g, '');
                // Se digitou com DDD (10 ou 11 dígitos) sem o 55:
                if (patientPhone.length === 10 || patientPhone.length === 11) {
                    patientPhone = '55' + patientPhone;
                } else if (!patientPhone.startsWith('55') && patientPhone.length > 0 && patientPhone.length < 10) {
                    phoneRejectionReason = 'Telefone incompleto (menos de 10 dígitos)';
                } else if (!patientPhone.startsWith('55') && patientPhone.length >= 10) {
                    patientPhone = '55' + patientPhone;
                }

                if (patientPhone.startsWith('55')) {
                    const dddAndNumber = patientPhone.slice(2);
                    // No Brasil: DDD tem 2 dígitos (11 a 99). Telefone fixo tem 8 dígitos começando com 2, 3, 4 ou 5.
                    if (dddAndNumber.length === 10 && /^[1-9]{2}[2-5]/.test(dddAndNumber)) {
                        phoneRejectionReason = 'Telefone Fixo (Não suporta WhatsApp)';
                    } else if (dddAndNumber.length === 11 && /^[1-9]{2}9/.test(dddAndNumber)) {
                        isPotentialWhatsApp = true;
                    } else if (dddAndNumber.length === 10 && /^[1-9]{2}[6-9]/.test(dddAndNumber)) {
                        // Celular antigo de 8 dígitos começando com 6 a 9: adicionar o 9
                        patientPhone = '55' + dddAndNumber.slice(0, 2) + '9' + dddAndNumber.slice(2);
                        isPotentialWhatsApp = true;
                    } else if (patientPhone.length >= 12 && patientPhone.length <= 13) {
                        isPotentialWhatsApp = true;
                    } else {
                        phoneRejectionReason = 'Formato de telefone incompatível com WhatsApp';
                    }
                } else if (patientPhone.length >= 10) {
                    isPotentialWhatsApp = true;
                }
            } else {
                phoneRejectionReason = 'Telefone não cadastrado';
            }

            // Fallback robusto para nome do procedimento: Cache -> Objeto -> Default
            const proc = this.dbService?.procedures().find((p: any) => p.id === appointment.procedureId);
            const procedureName = proc?.name || appointment.procedureName || 'Procedimento';

            if (!isPotentialWhatsApp) {
                console.warn(`[Webhook Anti-Ban] Paciente ${patientName} (${patientPhone || 'sem número'}): ${phoneRejectionReason}. Abortando envio para proteger o chip.`);
                try {
                    await supabase.from('webhook_logs').insert({
                        municipality_id: appointment.municipalityId,
                        appointment_id: appointment.id,
                        patient_name: patientName,
                        procedure_name: procedureName,
                        status: 'FAILED',
                        response_body: `[Proteção Anti-Ban] Envio cancelado: ${phoneRejectionReason}`,
                        attempts: 5,
                        created_at: new Date().toISOString()
                    });
                } catch (e) {
                    // Silencia erro em log de auditoria
                }
                return { success: false, pending: false };
            }

            const executor = appointment.executorName || 'Não informado';
            const location = appointment.schedulingData?.location || 'Não informado';

            const payload: any = {
                event: 'APPOINTMENT_SCHEDULED',
                appointment: appointment,
                patientId: appointment.patientId,
                patientName,
                patientPhone,
                procedureName,
                protocol: appointment.latestProtocol || '',
                status: appointment.status,
                location: location,
                executor: executor,
                timestamp: new Date().toISOString()
            };

            // 2. Processamento da URL (Placeholders)
            let finalUrl = resolvedWebhookUrl
                .replace(/{{patientName}}/g, encodeURIComponent(payload.patientName))
                .replace(/{{patientPhone}}/g, payload.patientPhone)
                .replace(/{{procedureName}}/g, encodeURIComponent(payload.procedureName))
                .replace(/{{protocol}}/g, encodeURIComponent(payload.protocol))
                .replace(/{{status}}/g, encodeURIComponent(payload.status))
                .replace(/{{location}}/g, encodeURIComponent(payload.location))
                .replace(/{{executor}}/g, encodeURIComponent(payload.executor));

            // 3. Custom Payload logic
            let finalBody: any = payload;
            if (currentMun.n8n_custom_payload) {
                try {
                    let customStr = currentMun.n8n_custom_payload
                        .replace(/{{patientName}}/g, payload.patientName)
                        .replace(/{{patientPhone}}/g, payload.patientPhone)
                        .replace(/{{procedureName}}/g, payload.procedureName)
                        .replace(/{{protocol}}/g, payload.protocol)
                        .replace(/{{status}}/g, payload.status)
                        .replace(/{{location}}/g, payload.location)
                        .replace(/{{executor}}/g, payload.executor)
                        .replace(/{{timestamp}}/g, payload.timestamp);
                    finalBody = JSON.parse(customStr);
                } catch (e) {
                    console.error('[Webhook] Erro ao processar JSON customizado:', e);
                    this.toast.show('Aviso: O JSON customizado de notificação possui erros de sintaxe. Usando payload padrão.', 'warning');
                }
            }

            // 4. Gravar log com status PENDING para ativar o Gatilho do Banco (Trigger)
            let logInsertSuccess = false;
            try {
                // Deduplicação por appointment_id (query segura, sem interpolação de strings)
                const { data: existingLogs, error: findError } = await supabase.from('webhook_logs')
                    .select('*')
                    .eq('appointment_id', appointment.id)
                    .order('created_at', { ascending: false });

                if (!findError && existingLogs && existingLogs.length > 0) {
                    const targetLog = existingLogs[0];
                    // Limpar logs duplicados antigos (manter apenas o mais recente)
                    if (existingLogs.length > 1) {
                        const idsToDelete = existingLogs.slice(1).map(l => l.id);
                        await supabase.from('webhook_logs').delete().in('id', idsToDelete);
                    }

                    const { error: updateError } = await supabase.from('webhook_logs').update({
                        status: 'PENDING', // REATIVAR GATILHO
                        payload: finalBody,
                        target_webhook_url: finalUrl,
                        attempts: (targetLog.attempts || 0) + 1,
                        last_attempt: new Date().toISOString()
                    }).eq('id', targetLog.id);

                    if (updateError) {
                        console.error('[Webhook Log] Erro ao atualizar log:', updateError);
                    } else {
                        logInsertSuccess = true;
                    }
                } else {
                    const { error: insertError } = await supabase.from('webhook_logs').insert({
                        municipality_id: appointment.municipalityId,
                        appointment_id: appointment.id,
                        patient_name: patientName,
                        procedure_name: procedureName,
                        status: 'PENDING', // ATIVAR GATILHO
                        target_webhook_url: finalUrl,
                        payload: finalBody,
                        attempts: 1,
                        created_at: new Date().toISOString()
                    });

                    if (insertError) {
                        console.error('[Webhook Log] Erro ao inserir log:', insertError);
                    } else {
                        logInsertSuccess = true;
                    }
                }
            } catch (e) {
                console.error('[Webhook Log] Erro ao gerenciar logs:', e);
            }

            return { success: logInsertSuccess, pending: logInsertSuccess };
        } catch (err: any) {
            console.error('[Webhook] Erro fatal no disparo:', err);
            return { success: false, pending: false };
        }
    }

    // --- CIRURGIA E CICLOS CIRÚRGICOS ---

    public surgicalCyclesData = signal<any[]>([]);

    async fetchSurgicalCycles(municipalityId: string, opts?: { search?: string; procedureId?: number | null; month?: string; }) {
        try {
            const { data, error } = await supabase.rpc('fetch_surgical_cycles', {
                p_municipality_id: municipalityId,
                p_search: opts?.search || null,
                p_procedure_id: opts?.procedureId ?? null,
                p_month: opts?.month || null
            });

            if (error) throw error;

            const mapped = (data || []).map((item: any) => this.mapToPatientProcess(item));
            this.surgicalCyclesData.set(mapped);
        } catch (err) {
            console.error('[AppointmentService] Erro ao buscar ciclos cirúrgicos:', err);
        }
    }

    async getSurgerySteps(procedureId: number): Promise<SurgeryStepConfig[]> {
        try {
            const { data, error } = await supabase
                .from('surgery_steps_config')
                .select('*, step_procedure:procedures!step_procedure_id(name)')
                .eq('parent_procedure_id', procedureId)
                .order('step_order', { ascending: true });

            if (error) throw error;
            return (data || []).map(d => ({
                id: d.id,
                parentProcedureId: d.parent_procedure_id,
                stepProcedureId: d.step_procedure_id,
                stepOrder: d.step_order,
                procedureName: d.step_procedure?.name || 'Procedimento não encontrado'
            }));
        } catch (e) {
            console.error(`[AppointmentService] Error fetching surgery steps:`, e);
            return [];
        }
    }

    async updateSurgerySteps(procedureId: number, steps: Omit<SurgeryStepConfig, 'id'>[]): Promise<void> {
        // 1. Delete existing
        const { error: delError } = await supabase
            .from('surgery_steps_config')
            .delete()
            .eq('parent_procedure_id', procedureId);

        if (delError) throw delError;

        // 2. Insert new
        if (steps.length > 0) {
            const { error: insError } = await supabase
                .from('surgery_steps_config')
                .insert(steps.map(s => ({
                    parent_procedure_id: procedureId,
                    step_procedure_id: s.stepProcedureId,
                    step_order: s.stepOrder
                })));

            if (insError) throw insError;
        }

        // 3. Atualizar a contagem no procedimento
        await supabase.from('procedures').update({ pre_surgery_count: steps.length }).eq('id', procedureId);
        
        if (this.dbService?.proceduresTable) {
             this.dbService.proceduresTable.update(list => list.map(p => 
                  p.id === procedureId ? { ...p, preSurgeryCount: steps.length } : p
             ));
        }
    }

    async createNextSurgeryStep(parentAppointmentId: number, nextStepIndex: number, operatorId: number): Promise<string | null> {
        if (!this.dbService) throw new Error('DatabaseService not linked');

        // 1. Fetch parent appointment
        const { data: parentRaw, error: pErr } = await supabase.from('appointments').select('*').eq('id', parentAppointmentId).single();
        if (pErr || !parentRaw) throw new Error('Parent appointment not found');
        const parent = this.mapDbToAppointment(parentRaw);

        // 2. Fetch steps config
        const steps = await this.getSurgerySteps(parent.procedureId);
        
        if (steps.length === 0) {
            console.error(`[AppointmentService] Nenhuma etapa configurada para a cirurgia ${parent.procedureId}. Abortando progressão automática.`);
            return null;
        }

        const stepConfig = steps[nextStepIndex];
        if (!stepConfig) {
            console.warn(`[AppointmentService] Etapa ${nextStepIndex} não encontrada para cirurgia ${parent.procedureId}.`);
            return null;
        }

        let subProtocol = `${parent.latestProtocol}.${nextStepIndex + 1}`;

        // 3. Contar tentativas anteriores (para adicionar sufixo de reinício _R1, _R2, etc)
        const { count: stepAttempts } = await supabase
            .from('appointments')
            .select('*', { count: 'exact', head: true })
            .eq('parent_appointment_id', parent.id)
            .eq('current_step_index', nextStepIndex);

        if (stepAttempts && stepAttempts > 0) {
            subProtocol = `${subProtocol}_R${stepAttempts}`;
        }

        // 4. Create new appointment for the step
        const stepAppointment: Appointment = {
            patientId: parent.patientId,
            procedureId: stepConfig.stepProcedureId,
            municipalityId: parent.municipalityId,
            status: 'Aguardando' as const,
            priority: parent.priority,
            registrationDate: new Date(),
            requestDate: parent.requestDate,
            requestImage: parent.requestImage,
            parentAppointmentId: parent.id,
            latestProtocol: subProtocol,
            subProtocol: subProtocol,
            currentStepIndex: nextStepIndex,
            isSurgeryParent: false
        } as Appointment;

        const { data: inserted, error: insError } = await supabase
            .from('appointments')
            .insert(this.mapAppointmentToDb(stepAppointment))
            .select()
            .single();

        if (insError) throw insError;

        // 5. Update parent's current step index
        await supabase.from('appointments').update({ current_step_index: nextStepIndex }).eq('id', parent.id);

        // Atualizar estado local
        this.appointmentsTable.update(list => list.map(a => a.id === parent.id ? { ...a, currentStepIndex: nextStepIndex } : a));

        // 6. Auditoria (Diretamente via AuditService)
        const patient = this.dbService?.patientsTable?.().find((p: any) => p.id === parent.patientId);
        const procedure = this.dbService?.proceduresTable?.().find((p: any) => p.id === stepConfig.stepProcedureId);
        
        const dadosNovos = {
            tipo: 'Etapa Cirúrgica',
            nomeProcedimento: stepConfig.procedureName,
            etapaAtual: nextStepIndex + 1,
            agendamentoPai: parent.latestProtocol,
            nomePaciente: patient?.name || patient?.social_name || '-',
            status: 'Em Andamento',
            prioridade: parent.priority,
            protocoloPai: parent.latestProtocol,
            subProtocolo: subProtocol
        };
        
        await this.auditService.registrarAuditoria(
            subProtocol,
            'CIRURGIA_ETAPA' as any,
            `Iniciada Etapa ${nextStepIndex + 1}: ${stepConfig.procedureName}`,
            operatorId,
            null,
            null,
            dadosNovos
        );

        this.appointmentsTable.update(list => [...list, this.mapDbToAppointment(inserted)]);

        return subProtocol;
    }

    async closeSurgicalCycle(parentAppointmentId: number, operatorId: number): Promise<void> {
        const { data: parent, error: pErr } = await supabase.from('appointments').select('*').eq('id', parentAppointmentId).single();
        if (pErr || !parent) throw new Error('Parent appointment not found');

        const { error } = await supabase
            .from('appointments')
            .update({ status: 'Cancelado', outcome_reason: 'Ciclo cirúrgico encerrado pelo regulador.' })
            .or(`id.eq.${parentAppointmentId},parent_appointment_id.eq.${parentAppointmentId}`)
            .in('status', ['Aguardando', 'Em Agendamento', 'Agendado']);

        if (error) throw error;

        if (this.auditService) {
            await this.auditService.registrarAuditoria(parent.latest_protocol, 'CANCELAMENTO', 'Ciclo cirúrgico encerrado totalmente.', operatorId);
        }

        this.appointmentsTable.update(list => list.map(a =>
            (a.id === parentAppointmentId || a.parentAppointmentId === parentAppointmentId) && ['Aguardando', 'Em Agendamento', 'Agendado'].includes(a.status)
                ? { ...a, status: 'Cancelado' } : a
        ));
    }

    async restartSurgicalCycle(parentAppointmentId: number, operatorId: number): Promise<void> {
        const { data: parent, error: pErr } = await supabase.from('appointments').select('*').eq('id', parentAppointmentId).single();
        if (pErr || !parent) throw new Error('Parent appointment not found');

        // Cancela TODAS as etapas do ciclo, inclusive as realizadas, para que o ciclo comece do zero
        await supabase
            .from('appointments')
            .update({ status: 'Cancelado', outcome_reason: 'Ciclo reiniciado.' })
            .eq('parent_appointment_id', parentAppointmentId)
            .neq('status', 'Cancelado');

        // Reseta o índice, as etapas completadas e o status do pai
        await supabase
            .from('appointments')
            .update({ current_step_index: 0, pre_surgery_completed: 0, status: 'Aguardando' })
            .eq('id', parentAppointmentId);

        // Atualiza estado local - Parent
        this.appointmentsTable.update(list => list.map(a =>
            a.id === parentAppointmentId
                ? { ...a, currentStepIndex: 0, preSurgeryCompleted: 0, status: 'Aguardando' as const }
                : a
        ));

        // Atualiza estado local - Children
        this.appointmentsTable.update(list => list.map(a =>
            (a.parentAppointmentId === parentAppointmentId && a.status !== 'Cancelado')
                ? { ...a, status: 'Cancelado' as const, outcomeData: { date: new Date().toISOString().split('T')[0], reason: 'Ciclo reiniciado.' } }
                : a
        ));

        // Cria a primeira etapa do novo ciclo
        await this.createNextSurgeryStep(parentAppointmentId, 0, operatorId);

        if (this.auditService) {
            await this.auditService.registrarAuditoria(parent.latest_protocol, 'MUDANCA_STATUS', 'Ciclo cirúrgico reiniciado para a Etapa 1.', operatorId);
        }
    }

    /**
     * Remove um agendamento e seus dependentes (slots, sub-etapas) com auditoria.
     */
    async deleteAppointmentWithLogic(id: number, operatorId: number) {
        if (!this.dbService) throw new Error('DatabaseService not linked');

        let oldApp = this.appointmentsTable().find(a => a.id === id);
        if (!oldApp) {
            const { data, error } = await supabase.from('appointments').select('*').eq('id', id).maybeSingle();
            if (error || !data) throw new Error('Atendimento não encontrado no banco de dados.');
            oldApp = this.mapDbToAppointment(data);
        }

        // 1. LIBERA A VAGA primeiro (operação atômica antes de qualquer outra coisa)
        //    Regra: ao excluir um agendamento, a vaga volta a estar disponível
        //    O slot permanece ativo no pool (distributed_count não muda)
        //    O trigger fn_safe_release_slot_on_appointment_change no banco também garante isso
        await supabase
            .from('agenda_slots')
            .update({
                is_available: true,
                appointment_id: null,
                patient_id: null
            })
            .eq('appointment_id', id);

        // 2. Exclusão recursiva de etapas filhas (ciclo cirúrgico) no banco
        const { data: dbChildren } = await supabase
            .from('appointments')
            .select('id')
            .eq('parent_appointment_id', id)
            .eq('is_active', true);

        if (dbChildren && dbChildren.length > 0) {
            for (const child of dbChildren) {
                await this.deleteAppointmentWithLogic(child.id, operatorId);
            }
        }

        // 3. Preserva o protocolo oficial original do agendamento
        let protocol = oldApp?.latestProtocol;
        if (!protocol) {
            protocol = await this.auditService.gerarProtocolo(
                'EXCLUSAO_LOGICA' as any,
                'AGENDAMENTO',
                id,
                operatorId,
                undefined,
                undefined,
                `Exclusão de agendamento ID: ${id}`
            );
        }

        const patient = this.dbService?.patientsTable?.().find((p: any) => p.id === oldApp.patientId);
        const procedure = this.dbService?.proceduresTable?.().find((p: any) => p.id === oldApp.procedureId);
        const dadosAnteriores = this.getOptimizedAuditData(oldApp, patient, procedure);
        const dadosNovos = {
            ...dadosAnteriores,
            status: 'Cancelado',
            motivoCancelamento: oldApp.outcomeData?.reason || 'Exclusão/Cancelamento pelo usuário',
            dataDesfecho: new Date().toISOString().split('T')[0]
        };

        // 4. Registra na auditoria preservando a cadeia de custódia do protocolo original
        await this.auditService.registrarAuditoria(
            protocol,
            'CANCELAMENTO' as any,
            `Cancelamento definitivo do atendimento #${id}. Protocolo: ${protocol}`,
            operatorId,
            undefined,
            dadosAnteriores,
            dadosNovos
        );

        // 5. Soft Delete: inativa o agendamento no banco mantendo o protocolo e data do desfecho
        const { error } = await supabase
            .from('appointments')
            .update({
                is_active: false,
                status: 'Cancelado',
                latest_protocol: protocol,
                outcome_date: new Date().toISOString().split('T')[0],
                outcome_reason: oldApp.outcomeData?.reason || 'Excluído logicamente pelo usuário'
            })
            .eq('id', id);
        if (error) throw error;

        // 6. Atualiza sinal local (remove da lista em memória)
        this.appointmentsTable.update(list => list.filter(a => a.id !== id));

        // 7. Sincroniza o estado do painel
        if (oldApp.municipalityId && this.dbService) {
            await this.dbService.syncBusinessState(oldApp.municipalityId);
        } else {
            this.triggerQueueRefresh();
        }

        return protocol;
    }

    /**
     * Envia um evento de teste para o Webhook configurado via Edge Function server-side,
     * validando a Máscara de IP (Proxy Anti-Ban) em modo Fail-Safe quando habilitada.
     */
    async testN8NWebhook(
        url: string,
        customPayload?: string,
        municipalityId?: string,
        draftMask?: MunicipalityIpMaskInput
    ): Promise<{ success: boolean; maskedExitIp?: string | null; proxyActive?: boolean }> {
        if (!url) throw new Error('URL do Webhook não configurada.');

        if (draftMask?.enabled) {
            const endpointCheck = isValidProxyEndpoint(draftMask.host, draftMask.port);
            if (!endpointCheck.valid) {
                throw new Error(`[Fail-Safe Máscara IP] ${endpointCheck.reason}`);
            }
        }

        const mockData: any = {
            patientName: 'Larissa Marinho (MOCK)',
            patientPhone: '5591999999999', // Já com código do país
            procedureName: 'NEUROLOGIA (EXTERNO)',
            protocol: 'MOCK-PROT-' + new Date().getTime(),
            status: 'Agendado',
            location: 'Hospital de Base (MOCK)',
            executor: 'Clínica de Teste (MOCK)',
            timestamp: new Date().toISOString()
        };

        let body: any = {
            event: 'TEST_WEBHOOK_CONNECTION',
            ...mockData,
            isTest: true
        };

        if (customPayload) {
            try {
                const customStr = customPayload
                    .replace(/{{patientName}}/g, mockData.patientName)
                    .replace(/{{patientPhone}}/g, mockData.patientPhone)
                    .replace(/{{procedureName}}/g, mockData.procedureName)
                    .replace(/{{protocol}}/g, mockData.protocol)
                    .replace(/{{status}}/g, mockData.status)
                    .replace(/{{location}}/g, mockData.location)
                    .replace(/{{executor}}/g, mockData.executor)
                    .replace(/{{timestamp}}/g, mockData.timestamp);
                body = JSON.parse(customStr);
            } catch (e) {
                console.warn('[Webhook Test] JSON customizado inválido, enviando padrão:', e);
            }
        }

        // 1. Execução preferencial Server-Side via Edge Function (protege IP do operador e valida Proxy Fail-Safe)
        const { data: edgeData, error: edgeError } = await supabase.functions.invoke('notify-n8n', {
            body: {
                mode: 'TEST',
                municipality_id: municipalityId || '',
                target_webhook_url: url,
                payload: body,
                draft_mask: draftMask ?? null
            }
        });

        if (!edgeError && edgeData?.success) {
            return {
                success: true,
                maskedExitIp: edgeData.maskedExitIp ?? null,
                proxyActive: Boolean(edgeData.proxyActive)
            };
        }

        if (edgeError) {
            let detailedMessage = '';
            try {
                const ctx = (edgeError as any).context;
                if (ctx && typeof ctx.json === 'function') {
                    const errJson = await ctx.json();
                    detailedMessage = errJson?.error || '';
                }
            } catch {
                // Ignora falha de parse do corpo de erro
            }

            // FAIL-SAFE ESTRITO: se a máscara de IP estiver ativa, NUNCA faz fallback direto pelo navegador
            if (draftMask?.enabled || detailedMessage) {
                throw new Error(
                    sanitizeSensitiveProxyData(
                        detailedMessage || 'Não foi possível validar o Proxy na Edge Function (Fail-Safe bloqueou envio direto).'
                    )
                );
            }
        }

        // 2. Fallback direto APENAS quando a Máscara de IP está DESATIVADA e a Edge Function ainda não respondeu o modo TEST
        let testStatus: 'SUCCESS' | 'FAILED' = 'FAILED';
        let responseBody = '';
        let responseStatus: number | null = null;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            responseStatus = response.status;
            responseBody = sanitizeSensitiveProxyData(await response.text().then(t => t.slice(0, 500)));
            testStatus = response.ok ? 'SUCCESS' : 'FAILED';

            if (!response.ok) {
                throw new Error(`Falha no n8n (Status: ${response.status} ${response.statusText})`);
            }
        } catch (err: any) {
            if (!responseBody) responseBody = sanitizeSensitiveProxyData(err.message);
            throw err;
        } finally {
            if (municipalityId) {
                try {
                    await supabase.from('webhook_logs').insert({
                        municipality_id: municipalityId,
                        appointment_id: null,
                        patient_name: 'TESTE DE CONEXÃO',
                        procedure_name: 'Webhook Test',
                        status: testStatus,
                        target_webhook_url: url,
                        payload: body,
                        response_status: responseStatus,
                        response_body: responseBody,
                        attempts: 1,
                        created_at: new Date().toISOString()
                    });
                } catch (logErr) {
                    console.warn('[Webhook Test] Falha ao registrar log de teste:', logErr);
                }
            }
        }

        return { success: true, maskedExitIp: null, proxyActive: false };
    }

    /**
     * Retorna a lista de agendamentos de um procedimento em uma data específica
     * para contadores de agendamento (Geral e por Local/Ente Executor).
     */
    async getProcedureScheduledStatsOnDate(
        procedureId: number,
        scheduleDate: string,
        municipalityId?: string | null
    ): Promise<{
        id: number;
        status: string;
        scheduleLocation: string | null;
        executorId: string | null;
        executorName: string | null;
    }[]> {
        if (!procedureId || !scheduleDate) return [];

        const normDate = AppointmentService.normalizeDate(scheduleDate);
        if (!normDate) return [];

        try {
            let query = supabase
                .from('appointments')
                .select('id, status, schedule_location, executor_id, executor_name')
                .eq('procedure_id', procedureId)
                .eq('schedule_date', normDate)
                .eq('is_active', true)
                .in('status', ['Agendado', 'Realizado']);

            if (municipalityId) {
                query = query.eq('municipality_id', municipalityId);
            }

            const { data, error } = await query;
            if (error) {
                console.error('[AppointmentService] Erro ao buscar agendados na data:', error);
                return [];
            }

            return (data || []).map((row: any) => ({
                id: row.id,
                status: row.status,
                scheduleLocation: row.schedule_location || null,
                executorId: row.executor_id || null,
                executorName: row.executor_name || null
            }));
        } catch (err) {
            console.error('[AppointmentService] Falha inesperada ao buscar agendados na data:', err);
            return [];
        }
    }

    /**
     * Dispara a verificação e expiração automática de agendamentos estagnados no status 'Em Agendamento' há mais de X dias.
     * Retorna a quantidade de procedimentos revertidos para a fila de espera ('Aguardando').
     */
    async checkAndExpireStaleScheduling(days: number = 7): Promise<number> {
        try {
            const { data, error } = await supabase.rpc('expire_stale_scheduling_appointments', {
                p_days: days
            });
            if (error) {
                console.warn('[AppointmentService] Erro ao verificar agendamentos expirados:', error);
                return 0;
            }
            const count = Number(data) || 0;
            if (count > 0) {
                console.info(`[AppointmentService] Limpeza automática: ${count} paciente(s) retornaram à fila de espera.`);
                this.triggerQueueRefresh();
            }
            return count;
        } catch (err) {
            console.warn('[AppointmentService] Falha ao expirar agendamentos:', err);
            return 0;
        }
    }
}
